/* Seller-specific settings panels (Branding + Domain) — split out so they
   can reuse the existing tenant endpoints. */
import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { FieldRow, SectionHeader, ComingSoon, FocusScroller } from "../../components/SettingsShell";
import DocUploadField from "../../components/DocUploadField";
import BrandingSlotHelper from "../../components/BrandingSlotHelper";
import BrandingSmartFill from "../../components/BrandingSmartFill";
import TestModeCard from "../../components/TestModeCard";
import EmailDomainCard from "./EmailDomainCard";
import { toast } from "sonner";
import { Copy, Check, Sparkles, Loader2, Globe, ExternalLink, AlertCircle, AlertTriangle, CheckCircle2, Download, Calendar, CalendarClock, Eye, Building2, QrCode, Code, MousePointerClick, Mail, Send, RefreshCw, Hash, Crown, X, Clock } from "lucide-react";

// Tight sub-section divider used inside the Public profile panel to group
// related fields without inflating the spacing.
function SubSectionHeader({ title, desc }) {
  return (
    <div className="mt-7 mb-3 pt-5 border-t border-foreground/10">
      <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">{title}</div>
      {desc && <div className="text-[12.5px] text-foreground/65 mt-1 leading-snug">{desc}</div>}
    </div>
  );
}

// ── Branding ───────────────────────────────────────────────────────────
export function BrandingPanel() {
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({
    name: "",
    logo_url: "",
    logo_square_url: "",
    logo_dark_url: "",
    cover_image_url: "",
    primary_color: "#0a1f1c",
    accent_color: "#e8ff3a",
    hero_title: "",
    hero_subtitle: "",
  });
  const [saving, setSaving] = useState(false);
  // Tracks whether the user explicitly clicked "Save anyway" when only
  // yellow warnings (not red blockers) are present. We re-arm it when
  // the form changes so the seller has a chance to fix the warnings.
  const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);

  // ── Tiered validation ─────────────────────────────────────────────────
  // 🔴 BLOCKING — bare minimum for a functional brand. Save button is
  //    disabled and shown with a red banner until ALL of these are valid.
  // 🟡 WARNING — recommended; we can fall back to a default, but the
  //    seller should know they're not set. Save works, but a yellow
  //    summary at the top says "save anyway?".
  // ⚪ Dark-mode logo is intentionally NOT here — only some brands need it.
  const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
  const v = (() => {
    const blocking = [];
    const warnings = [];

    if (!form.name || form.name.trim().length < 2) {
      blocking.push({ key: "name", label: "Brand name",
        hint: "Required — buyers see this in every email, sidebar, hero, and invoice." });
    }
    if (!form.primary_color || !HEX_RE.test(form.primary_color.trim())) {
      blocking.push({ key: "primary_color", label: "Primary color",
        hint: "Required — used across every branded surface. Must be a valid hex color." });
    }
    if (!form.logo_url) {
      blocking.push({ key: "logo_url", label: "Logo · primary",
        hint: "Required — your wide horizontal logo. Used in the sidebar, invoices, and email headers." });
    }

    if (!form.logo_square_url) {
      warnings.push({ key: "logo_square_url", label: "Logo · square (monogram)",
        hint: "Recommended — used as the browser favicon. We'll auto-generate a monogram from your name + primary color if you skip this, but a real square logo looks better." });
    }
    if (!form.cover_image_url) {
      warnings.push({ key: "cover_image_url", label: "Cover image",
        hint: "Recommended — the storefront hero background. We'll use a gradient banner if missing." });
    }
    if (!form.hero_title || form.hero_title.trim().length < 2) {
      warnings.push({ key: "hero_title", label: "Hero title",
        hint: "Recommended — the big headline buyers see when they enter your storefront. Falls back to \"{Brand} — Wholesale\" if empty." });
    }
    if (!form.hero_subtitle || form.hero_subtitle.trim().length < 2) {
      warnings.push({ key: "hero_subtitle", label: "Hero subtitle",
        hint: "Recommended — supporting copy under the hero title. Falls back to a generic verqo line if empty." });
    }
    if (!form.accent_color || !HEX_RE.test(form.accent_color.trim())) {
      warnings.push({ key: "accent_color", label: "Accent color",
        hint: "Recommended — the accent badge on your storefront hero. Defaults to a neutral if missing." });
    }

    return { blocking, warnings };
  })();

  const hasBlocking = v.blocking.length > 0;
  const hasWarnings = v.warnings.length > 0;
  const canSave = !hasBlocking && (!hasWarnings || warningsAcknowledged);

  // Re-arm the warnings if form mutates after the user clicked "Save anyway".
  // (Keeps the UX honest: changes mean the seller has a chance to fix the
  // warnings before saving again.)
  useEffect(() => {
    if (warningsAcknowledged && hasWarnings) setWarningsAcknowledged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.name, form.logo_url, form.logo_square_url, form.logo_dark_url,
      form.cover_image_url, form.primary_color, form.accent_color,
      form.hero_title, form.hero_subtitle]);

  // Used by the warning summary's "Fix now" buttons.
  const scrollToField = (key) => {
    const el = document.getElementById(`brand-field-${key}`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    // Briefly highlight the row so the eye lands on it
    el.classList.add("ring-2", "ring-amber-400", "ring-offset-2");
    setTimeout(() => {
      el.classList.remove("ring-2", "ring-amber-400", "ring-offset-2");
    }, 1600);
  };

  // Lookups for the per-field red-flag pill helper.
  const blockingKeys = new Set(v.blocking.map(b => b.key));
  const warningKeys = new Set(v.warnings.map(w => w.key));
  const fieldFlag = (key) => {
    if (blockingKeys.has(key)) return "blocking";
    if (warningKeys.has(key))  return "warning";
    return null;
  };

  const reloadTenant = async () => {
    const r = await api.get("/tenant/me");
    const t = r.data; setTenant(t);
    setForm((f) => ({
      ...f,
      name: t.name || "",
      logo_url:        t.branding?.logo_url || "",
      logo_square_url: t.branding?.logo_square_url || "",
      logo_dark_url:   t.branding?.logo_dark_url || "",
      cover_image_url: t.branding?.cover_image_url || "",
      primary_color: t.branding?.primary_color || "#0a1f1c",
      accent_color: t.branding?.accent_color || "#e8ff3a",
      hero_title: t.branding?.hero_title || "",
      hero_subtitle: t.branding?.hero_subtitle || "",
    }));
  };

  useEffect(() => { reloadTenant(); }, []);

  const save = async () => {
    // Belt-and-suspenders: the Save button is already disabled when
    // blocking fields are missing or yellow warnings haven't been
    // acknowledged. But a determined power-user could re-enable the
    // button via devtools. Re-check here so we never persist broken
    // branding.
    if (!canSave) {
      if (hasBlocking) {
        toast.error(
          `Can't save — ${v.blocking.length} required field${v.blocking.length === 1 ? "" : "s"} missing: ${v.blocking.map(b => b.label).join(", ")}`,
          { duration: 6000 },
        );
      }
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch("/tenant/me", {
        name: form.name,
        branding: {
          logo_url:        form.logo_url || null,
          logo_square_url: form.logo_square_url || null,
          logo_dark_url:   form.logo_dark_url || null,
          cover_image_url: form.cover_image_url || null,
          primary_color: form.primary_color,
          accent_color: form.accent_color,
          hero_title: form.hero_title,
          hero_subtitle: form.hero_subtitle,
        },
      });
      // Sync local state with what the server actually persisted — so the
      // user sees the same values that buyers will see.
      setTenant(data);
      setForm((f) => ({
        ...f,
        name: data.name || f.name,
        logo_url:        data.branding?.logo_url || "",
        logo_square_url: data.branding?.logo_square_url || "",
        logo_dark_url:   data.branding?.logo_dark_url || "",
        cover_image_url: data.branding?.cover_image_url || "",
        primary_color: data.branding?.primary_color || f.primary_color,
        accent_color: data.branding?.accent_color || f.accent_color,
        hero_title: data.branding?.hero_title || "",
        hero_subtitle: data.branding?.hero_subtitle || "",
      }));
      setWarningsAcknowledged(false);  // re-arm so next mutation re-prompts
      toast.success("Branding saved — buyers will see the new theme instantly.");
    } catch (e) {
      // Surface the real backend reason instead of a silent failure. The
      // most common one in production is the team-role gate (only the
      // tenant owner can change branding) which used to fail invisibly.
      const detail = e?.response?.data?.detail;
      let msg = "Couldn't save branding";
      if (typeof detail === "string") msg = detail;
      else if (detail?.message) msg = detail.message;
      else if (detail?.reason === "team_role_blocked") msg = "Branding can only be changed by the account owner — ask the owner to grant Settings permission to your team role.";
      else if (e?.response?.status === 401) msg = "Session expired — please sign in again.";
      else if (e?.message) msg = e.message;
      toast.error(msg, { duration: 6000 });
    } finally { setSaving(false); }
  };

  if (!tenant) return <Loader2 className="w-4 h-4 animate-spin text-foreground/40"/>;

  return (
    <div>
      <SectionHeader title="Branding" desc="How your invited buyers see your brand inside their verqo portal. When they switch to your supplier context, the sidebar re-skins with your logo and colors."/>

      {/* One-click setup — Stripe/Linear-style "import or generate" */}
      <BrandingSmartFill onChanged={reloadTenant} />

      {/* Validation summary — red blocker bar or yellow warning bar */}
      {(hasBlocking || hasWarnings) && (
        <div
          className={`rounded-2xl border px-5 py-4 mb-5 ${
            hasBlocking
              ? "border-rose-300 bg-rose-50/60"
              : "border-amber-300 bg-amber-50/50"
          }`}
          data-testid={hasBlocking ? "branding-validation-blocking" : "branding-validation-warnings"}
        >
          <div className="flex items-start gap-3">
            <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${
              hasBlocking ? "bg-rose-500 text-white" : "bg-amber-500 text-white"
            }`}>
              {hasBlocking ? <AlertCircle className="w-5 h-5"/> : <AlertTriangle className="w-5 h-5"/>}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-[13px] font-semibold ${hasBlocking ? "text-rose-900" : "text-amber-900"}`}>
                {hasBlocking
                  ? `Can't save yet — ${v.blocking.length} required field${v.blocking.length === 1 ? "" : "s"} missing`
                  : `${v.warnings.length} recommended field${v.warnings.length === 1 ? "" : "s"} not set`}
              </div>
              <div className={`text-[11.5px] mt-0.5 leading-snug ${hasBlocking ? "text-rose-900/80" : "text-amber-900/80"}`}>
                {hasBlocking
                  ? "These fields are required so buyers see a functional brand. Add each missing field below to enable saving."
                  : "You can save anyway — these will fall back to sensible defaults. But buyers will see auto-generated stand-ins for the missing ones."}
              </div>
              <ul className="mt-2.5 space-y-1.5">
                {[...v.blocking, ...v.warnings].map((item) => {
                  const isBlock = blockingKeys.has(item.key);
                  return (
                    <li
                      key={item.key}
                      className="flex items-start gap-2 text-[12px]"
                      data-testid={`branding-validation-row-${item.key}`}
                    >
                      <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${
                        isBlock ? "bg-rose-500" : "bg-amber-500"
                      }`}/>
                      <div className="flex-1 min-w-0">
                        <button
                          type="button"
                          onClick={() => scrollToField(item.key)}
                          className={`font-medium hover:underline ${
                            isBlock ? "text-rose-900" : "text-amber-900"
                          }`}
                          data-testid={`branding-fix-now-${item.key}`}
                        >
                          {item.label}
                        </button>
                        <span className={`ml-1 ${isBlock ? "text-rose-900/70" : "text-amber-900/70"}`}>
                          — {item.hint}
                        </span>
                      </div>
                    </li>
                  );
                })}
              </ul>
              {hasWarnings && !hasBlocking && !warningsAcknowledged && (
                <button
                  type="button"
                  onClick={() => setWarningsAcknowledged(true)}
                  data-testid="branding-acknowledge-warnings"
                  className="mt-3 text-[12px] font-medium text-amber-900 underline underline-offset-2 hover:text-amber-700"
                >
                  Save anyway — I&apos;ll fix these later →
                </button>
              )}
              {hasWarnings && !hasBlocking && warningsAcknowledged && (
                <div className="mt-3 text-[12px] text-emerald-700 font-medium flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5"/>
                  Acknowledged — Save is now enabled.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div>
        <div id="brand-field-name">
        <FieldRow label={<FieldLabel base="Brand name" flag={fieldFlag("name")}/>} hint="Buyers see this in their supplier switcher">
          <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="branding-name"/>
        </FieldRow>
        </div>
        <div id="brand-field-logo_url">
        <FieldRow label={<FieldLabel base="Logo · primary" flag={fieldFlag("logo_url")}/>} hint="Used in the sidebar, invoices, and email headers when buyers are inside your portal. PNG recommended — transparent backgrounds sit cleanly on every surface. Horizontal logos (wider than tall) work best here.">
          <DocUploadField
            docType="logo"
            accept="image/png,image/webp,image/jpeg,image/jpg"
            acceptLbl="PNG (recommended), WEBP, JPG"
            testIdPrefix="branding-logo"
            onChange={() => { void reloadTenant(); }}
          />
          <BrandingSlotHelper
            variant="logo_primary"
            currentUrl={form.logo_url}
            primary={form.primary_color}
            accent={form.accent_color}
            name={form.name}
          />
        </FieldRow>
        </div>
        <div id="brand-field-logo_square_url">
        <FieldRow label={<FieldLabel base="Logo · square (monogram)" flag={fieldFlag("logo_square_url")}/>} hint="A square version of your mark — used as the browser favicon when buyers are in your portal, in round profile chips, and the mobile app icon. Square PNG (256×256 or larger) works best. If you don't upload one, we use the primary logo cropped.">
          <DocUploadField
            docType="logo_square"
            accept="image/png,image/webp,image/jpeg,image/jpg"
            acceptLbl="PNG (recommended), WEBP, JPG · 1:1 ratio"
            testIdPrefix="branding-logo-square"
            onChange={() => { void reloadTenant(); }}
          />
          <BrandingSlotHelper
            variant="logo_square"
            currentUrl={form.logo_square_url}
            primary={form.primary_color}
            accent={form.accent_color}
            name={form.name}
          />
        </FieldRow>
        </div>
        <div id="brand-field-logo_dark_url">
        <FieldRow label="Logo · dark mode" hint="A light/white version of your logo for use on dark backgrounds (the supplier hero, the dark sidebar variant). Upload only if your primary logo has dark artwork that disappears on dark surfaces.">
          <DocUploadField
            docType="logo_dark"
            accept="image/png,image/webp,image/jpeg,image/jpg"
            acceptLbl="PNG with transparent background"
            testIdPrefix="branding-logo-dark"
            onChange={() => { void reloadTenant(); }}
          />
          <BrandingSlotHelper
            variant="logo_dark"
            currentUrl={form.logo_dark_url}
            primary={form.primary_color}
            accent={form.accent_color}
            name={form.name}
          />
        </FieldRow>
        </div>
        <div id="brand-field-cover_image_url">
        <FieldRow label={<FieldLabel base="Cover image" flag={fieldFlag("cover_image_url")}/>} hint="1600×400 lifestyle banner — appears as the hero background on your storefront when buyers land in your portal. Falls back to a primary-color gradient if not set. Use a wide photo of your product in-context or your retail space — NOT a logo lockup.">
          <DocUploadField
            docType="cover_image"
            accept="image/png,image/webp,image/jpeg,image/jpg"
            acceptLbl="PNG, WEBP, JPG · 1600×400 recommended"
            testIdPrefix="branding-cover"
            onChange={() => { void reloadTenant(); }}
          />
          <BrandingSlotHelper
            variant="cover_image"
            currentUrl={form.cover_image_url}
            primary={form.primary_color}
            accent={form.accent_color}
            name={form.name}
          />
        </FieldRow>
        </div>
        <div id="brand-field-primary_color">
        <FieldRow label={<FieldLabel base="Primary color" flag={fieldFlag("primary_color")}/>} hint="Your main brand color (background, accents)">
          <div className="flex items-center gap-2">
            <input type="color" value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} className="h-10 w-10 rounded border border-foreground/15" data-testid="branding-primary"/>
            <Input value={form.primary_color} onChange={e => setForm({...form, primary_color: e.target.value})} className="font-mono"/>
          </div>
        </FieldRow>
        </div>
        <div id="brand-field-accent_color">
        <FieldRow label={<FieldLabel base="Accent color" flag={fieldFlag("accent_color")}/>} hint="Highlights, buttons, active states">
          <div className="flex items-center gap-2">
            <input type="color" value={form.accent_color} onChange={e => setForm({...form, accent_color: e.target.value})} className="h-10 w-10 rounded border border-foreground/15" data-testid="branding-accent"/>
            <Input value={form.accent_color} onChange={e => setForm({...form, accent_color: e.target.value})} className="font-mono"/>
          </div>
        </FieldRow>
        </div>
        <div id="brand-field-hero_title">
        <FieldRow label={<FieldLabel base="Hero title" flag={fieldFlag("hero_title")}/>} hint="Seen by buyers when they first land in your context">
          <Input value={form.hero_title} onChange={e => setForm({...form, hero_title: e.target.value})} placeholder="Your tagline" data-testid="branding-hero-title"/>
        </FieldRow>
        </div>
        <div id="brand-field-hero_subtitle">
        <FieldRow label={<FieldLabel base="Hero subtitle" flag={fieldFlag("hero_subtitle")}/>}>
          <Textarea value={form.hero_subtitle} onChange={e => setForm({...form, hero_subtitle: e.target.value})} rows={2} data-testid="branding-hero-sub"/>
        </FieldRow>
        </div>
      </div>

      {/* Live preview — links to the public seller storefront which
          shows the buyer-facing branded surface (logo, colors, hero,
          policies). Reliable for every seller because slug is always
          generated at signup. Custom subdomains (white-label) are
          opt-in and not always wired to a real branded buyer login. */}
      <div className="mt-6 rounded-xl overflow-hidden border border-border">
        <div className="text-[10px] mono uppercase tracking-widest bg-secondary/40 px-3 py-2 flex items-center justify-between">
          <span className="text-foreground/55">Live preview</span>
          <a
            href={tenant?.slug ? `/s/${tenant.slug}` : "#"}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[10px] text-foreground/70 hover:text-foreground normal-case tracking-normal font-medium"
            data-testid="branding-preview-as-buyer"
          >
            <Eye className="w-3 h-3"/>
            <span>View as a buyer would see it</span>
            <ExternalLink className="w-2.5 h-2.5"/>
          </a>
        </div>
        <div
          className="p-7 relative overflow-hidden"
          style={{
            background: form.cover_image_url
              ? `linear-gradient(135deg, ${form.primary_color}cc, ${form.primary_color}80), url('${form.cover_image_url}') center/cover`
              : form.primary_color,
            color: "#fff",
            minHeight: 200,
          }}
        >
          <div className="flex items-center gap-2.5 relative z-10">
            {(form.logo_dark_url || form.logo_url) ? (
              <img src={form.logo_dark_url || form.logo_url} alt="" className="h-9"/>
            ) : (
              <span
                className="w-9 h-9 rounded-md grid place-items-center font-bold display"
                style={{ background: form.accent_color, color: form.primary_color }}
              >
                {(form.name || "?").slice(0,1)}
              </span>
            )}
            <span className="display font-semibold text-lg">{form.name || "Your brand"}</span>
          </div>
          <h3 className="display text-2xl mt-5 tracking-tight leading-tight relative z-10">{form.hero_title || "Your tagline here"}</h3>
          <p className="text-white/80 mt-2 text-[13px] relative z-10">{form.hero_subtitle || "Your supporting line."}</p>
          <span
            className="inline-block mt-4 px-3 py-1 rounded-full text-[11px] mono relative z-10"
            style={{ background: form.accent_color, color: form.primary_color }}
          >
            ACCENT
          </span>
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 mt-4">
        {hasBlocking && (
          <span className="text-[11.5px] text-rose-700 mono" data-testid="branding-save-blocked-reason">
            {v.blocking.length} required field{v.blocking.length === 1 ? "" : "s"} missing
          </span>
        )}
        {!hasBlocking && hasWarnings && !warningsAcknowledged && (
          <span className="text-[11.5px] text-amber-800 mono" data-testid="branding-save-blocked-warnings">
            {v.warnings.length} warning{v.warnings.length === 1 ? "" : "s"} — acknowledge above to save
          </span>
        )}
        <Button
          onClick={save}
          disabled={saving || !canSave}
          data-testid="branding-save"
          className="rounded-full bg-foreground text-background disabled:opacity-50 disabled:cursor-not-allowed"
          title={!canSave ? (hasBlocking ? "Add the required fields first" : "Acknowledge the warnings above to enable save") : ""}
        >
          {saving ? "Saving…" : "Save branding"}
        </Button>
      </div>
    </div>
  );
}

// Small label helper — renders the base label string plus a Required/
// Recommended pill when the field is currently in violation, so the
// seller sees the red flag at the field level too (not just in the
// summary bar).
function FieldLabel({ base, flag }) {
  if (!flag) return base;
  const isBlock = flag === "blocking";
  return (
    <span className="inline-flex items-center gap-2">
      {base}
      <span
        className={`text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded ${
          isBlock
            ? "bg-rose-100 text-rose-800 border border-rose-200"
            : "bg-amber-100 text-amber-800 border border-amber-200"
        }`}
        data-testid={`branding-flag-${isBlock ? "required" : "recommended"}`}
      >
        {isBlock ? "Required" : "Recommended"}
      </span>
    </span>
  );
}

// ── Domain helpers ─────────────────────────────────────────────────────
// Extract apex domain (e.g. "glowco.com") from a website URL or hostname.
// Returns null when we can't infer a usable apex.
function extractApexDomain(raw) {
  if (!raw || typeof raw !== "string") return null;
  let s = raw.trim().toLowerCase();
  if (!s) return null;
  s = s.replace(/^https?:\/\//, "").replace(/^www\./, "");
  s = s.split("/")[0].split("?")[0].split("#")[0].trim();
  if (!s || !s.includes(".")) return null;
  // Reject obvious nonsense (spaces / leading dot)
  if (/\s/.test(s) || s.startsWith(".")) return null;
  return s;
}

// Common TLD-typo corrections — most reported in support tickets across Stripe/Shopify
// and matched by Google Domains' inline suggester.
const TLD_TYPO_FIXES = {
  con: "com", cmo: "com", cm: "com", co: "com", som: "com", clm: "com", comm: "com",
  ney: "net", nte: "net", nett: "net",
  ogr: "org", og: "org", orgg: "org",
  io2: "io", io1: "io",
  cco: "co", coo: "co",
};
// Reserved / dev-only TLDs that can't host a real public site → reject outright.
const RESERVED_TLDS = new Set(["test", "example", "invalid", "localhost", "local"]);

// Real-time validator for the custom-domain input. Pure (no network) — returns
// { ok, error, warning, suggestion } so the UI can render inline state.
//   error      → blocks Save
//   warning    → non-blocking, suggestive
//   suggestion → "Did you mean …?" string the UI offers as a one-click apply
function validateCustomDomain(raw, ctx = {}) {
  const s = (raw || "").trim().toLowerCase();
  if (!s) return { ok: false, error: null }; // empty = no error, just disabled Save

  // Reject protocol / path / query / spaces — the input should be a bare hostname.
  if (/^https?:\/\//.test(s)) {
    return { ok: false, error: "Don't include https:// — just the hostname.", suggestion: s.replace(/^https?:\/\//, "").split("/")[0] };
  }
  if (s.startsWith("www.")) {
    return { ok: false, error: "Drop the leading www. — use the bare host.", suggestion: s.replace(/^www\./, "") };
  }
  if (s.includes("/") || s.includes("?") || s.includes("#") || /\s/.test(s)) {
    return { ok: false, error: "Hostname only — no paths, query strings, or spaces." };
  }
  if (s.startsWith(".") || s.endsWith(".") || s.includes("..")) {
    return { ok: false, error: "Domain has misplaced dots." };
  }

  const labels = s.split(".");
  if (labels.length < 2) {
    return { ok: false, error: "Use a full hostname like shop.yourbrand.com." };
  }
  // Per-label hostname rules: 1-63 chars, ASCII letters/digits/hyphen, no leading/trailing hyphen.
  for (const label of labels) {
    if (!label) return { ok: false, error: "Empty label between dots." };
    if (label.length > 63) return { ok: false, error: `"${label}" is over 63 characters.` };
    if (!/^[a-z0-9-]+$/.test(label)) {
      return { ok: false, error: `"${label}" has invalid characters. Letters, digits, hyphens only.` };
    }
    if (label.startsWith("-") || label.endsWith("-")) {
      return { ok: false, error: `"${label}" can't start or end with a hyphen.` };
    }
  }
  if (s.length > 253) return { ok: false, error: "Domain is too long (max 253 characters)." };

  // TLD typo? Offer "Did you mean …?"
  const tld = labels[labels.length - 1];
  if (TLD_TYPO_FIXES[tld]) {
    const fixed = [...labels.slice(0, -1), TLD_TYPO_FIXES[tld]].join(".");
    return { ok: false, error: `".${tld}" looks like a typo.`, suggestion: fixed };
  }
  if (RESERVED_TLDS.has(tld)) {
    return { ok: false, error: `".${tld}" is reserved and can't be used for a live site.` };
  }
  if (!/^[a-z]{2,}$/.test(tld)) {
    return { ok: false, error: `".${tld}" isn't a valid TLD.` };
  }

  // Apex (only 2 labels like "glowco.com") — CF for SaaS prefers a subdomain.
  if (labels.length === 2) {
    return {
      ok: true,
      warning: "Apex domains need DNS flattening — most registrars don't support a CNAME on the root. We strongly recommend a subdomain like shop.yourbrand.com.",
    };
  }

  // Collision with the seller's own verqo subdomain.
  if (ctx.subdomain && s === ctx.subdomain.toLowerCase()) {
    return { ok: false, error: "That's already your verqo subdomain — pick a different host." };
  }

  // Forbid verqo.com subdomains as "custom" — those are auto-managed.
  if (s.endsWith(".verqo.com")) {
    return { ok: false, error: "verqo.com subdomains are auto-managed. Use a domain you own." };
  }

  return { ok: true };
}

// Suggest 5 common B2B subdomain patterns the seller can one-click into the input.
const SUBDOMAIN_PRESETS = [
  { prefix: "wholesale", label: "Wholesale portal", hint: "Most popular" },
  { prefix: "shop",      label: "Buyer storefront",  hint: null },
  { prefix: "pay",       label: "Payments / invoices", hint: null },
  { prefix: "orders",    label: "Orders & POs",      hint: null },
  { prefix: "b2b",       label: "B2B channel",       hint: null },
];

function SuggestedDomains({ tenant, current, onPick }) {
  const apex = useMemo(() => {
    const fromWebsite = extractApexDomain(tenant?.settings?.profile?.website);
    if (fromWebsite) return fromWebsite;
    // Fallback: try slug — e.g. "glowco-beauty" → "glowco-beauty.com" (placeholder).
    const slug = (tenant?.slug || "").trim().toLowerCase();
    if (slug && !slug.includes(".")) return `${slug}.com`;
    return null;
  }, [tenant]);

  if (!apex) {
    return (
      <div className="mt-2 text-[11.5px] text-foreground/55" data-testid="domain-suggestions-empty">
        💡 Add your website under <strong>Public profile</strong> to get one-click subdomain suggestions
        (e.g. <code className="font-mono">wholesale.yourbrand.com</code>).
      </div>
    );
  }

  return (
    <div className="mt-3" data-testid="domain-suggestions">
      <div className="text-[11px] mono uppercase tracking-widest text-foreground/55 mb-2">
        Suggested for {apex}
      </div>
      <div className="flex flex-wrap gap-2">
        {SUBDOMAIN_PRESETS.map(p => {
          const full = `${p.prefix}.${apex}`;
          const active = current?.toLowerCase() === full;
          return (
            <button
              type="button"
              key={p.prefix}
              onClick={() => onPick(full)}
              data-testid={`domain-suggestion-${p.prefix}`}
              className={`group text-left rounded-lg border px-3 py-2 transition-colors ${
                active
                  ? "border-foreground bg-foreground text-background"
                  : "border-border hover:border-foreground/40 hover:bg-secondary/40"
              }`}
            >
              <div className={`font-mono text-[12.5px] ${active ? "" : "text-foreground"}`}>{full}</div>
              <div className={`text-[10.5px] mt-0.5 ${active ? "text-background/70" : "text-foreground/55"}`}>
                {p.label}{p.hint ? ` · ${p.hint}` : ""}
              </div>
            </button>
          );
        })}
      </div>
      <div className="text-[11px] text-foreground/55 mt-2">
        Or type any custom subdomain above (e.g. <code className="font-mono">portal.{apex}</code>, <code className="font-mono">buy.{apex}</code>).
      </div>
    </div>
  );
}

// ── Portal Launch Kit ─────────────────────────────────────────────────
// Drop-in assets sellers paste into their existing website / signature /
// social to drive buyers into the wholesale portal. Backed by
// GET /api/seller/portal-link which returns the canonical URL + a
// pre-rendered QR PNG + brand metadata (name, primary color, logo).
// Each tab generates a snippet template on the fly so a logo / brand
// edit propagates instantly — no caching surface to invalidate.

function CopyBlock({ text, testId, label = "Copy" }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Couldn't copy — select and copy manually");
    }
  };
  return (
    <Button
      type="button" size="sm" variant="outline"
      onClick={onCopy} data-testid={testId}
      className="rounded-full"
    >
      {copied ? <Check className="w-3.5 h-3.5"/> : <Copy className="w-3.5 h-3.5"/>}
      <span className="ml-1.5">{copied ? "Copied" : label}</span>
    </Button>
  );
}

function LaunchKitTab({ icon: Icon, label, active, onClick, testId }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testId}
      className={`flex items-center gap-2 px-3.5 py-2 rounded-full text-[13px] transition-colors ${
        active
          ? "bg-foreground text-background"
          : "text-foreground/65 hover:bg-secondary hover:text-foreground"
      }`}
    >
      <Icon className="w-3.5 h-3.5"/>
      <span>{label}</span>
    </button>
  );
}

function htmlBannerSnippet({ url, brandName, primaryColor }) {
  // Inline-styled so it renders identically across Shopify / Webflow /
  // WordPress / static-HTML — no external CSS dependency.
  return `<!-- verqo wholesale portal banner -->
<a href="${url}" target="_blank" rel="noopener" style="display:flex;align-items:center;justify-content:center;gap:10px;padding:14px 20px;background:${primaryColor};color:#fff;text-decoration:none;font-family:Inter,system-ui,sans-serif;font-size:14px;font-weight:600;letter-spacing:0.01em;border-radius:10px;box-shadow:0 4px 14px rgba(0,0,0,0.08);">
  <span>📦 Buying wholesale? Open ${brandName}'s portal</span>
  <span style="opacity:0.85;font-weight:500;">→</span>
</a>`;
}

function floatingWidgetSnippet({ url, brandName, primaryColor }) {
  // One-line drop-in. Uses a single style block to avoid leaking colors
  // and a single anchor to avoid auth/iframe headaches.
  return `<!-- verqo floating wholesale button -->
<style>
  .verqo-fab{position:fixed;right:24px;bottom:24px;z-index:9999;display:flex;align-items:center;gap:10px;padding:14px 22px;background:${primaryColor};color:#fff;text-decoration:none;font-family:Inter,system-ui,sans-serif;font-size:14px;font-weight:600;border-radius:999px;box-shadow:0 12px 28px rgba(0,0,0,0.18);transition:transform .15s ease, box-shadow .15s ease}
  .verqo-fab:hover{transform:translateY(-1px);box-shadow:0 16px 36px rgba(0,0,0,0.22)}
  @media(max-width:640px){.verqo-fab{right:16px;bottom:16px;padding:12px 18px;font-size:13px}}
</style>
<a class="verqo-fab" href="${url}" target="_blank" rel="noopener" aria-label="Open ${brandName} wholesale portal">
  <span style="font-size:16px;line-height:1">📦</span>
  <span>Wholesale</span>
</a>`;
}

function emailSignatureSnippet({ url, brandName, primaryColor }) {
  // Renders correctly inside Gmail / Outlook / Apple Mail signature
  // editors. Table-based for legacy MUA support.
  const safeHost = url.replace(/^https?:\/\//, "");
  return `<table cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,Helvetica,sans-serif;font-size:13px;color:#111;">
  <tr>
    <td style="padding-top:10px;border-top:1px solid #e5e7eb;">
      <div style="font-weight:600;color:${primaryColor};">📦 Wholesale orders</div>
      <div style="margin-top:2px;color:#6b7280;">
        Buy ${brandName} for your store — invoices, terms &amp; tracking in one place.
      </div>
      <div style="margin-top:6px;">
        <a href="${url}" style="color:${primaryColor};text-decoration:none;font-weight:600;">
          ${safeHost} →
        </a>
      </div>
    </td>
  </tr>
</table>`;
}

export function PortalLaunchKitPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("link");

  // Single source of truth for the fetch — used both on mount and when
  // the DomainPanel emits a `portal-link-refresh` event after saving,
  // disconnecting, or DNS-activating a custom domain. Keeps the
  // displayed URL in sync without a page reload.
  const reload = () => {
    let alive = true;
    setLoading(true);
    api.get("/seller/portal-link")
      .then(r => { if (alive) setData(r.data); })
      .catch(() => { if (alive) toast.error("Couldn't load portal link"); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  };

  useEffect(() => {
    const cleanup = reload();
    const onRefresh = () => reload();
    window.addEventListener("portal-link-refresh", onRefresh);
    return () => { cleanup?.(); window.removeEventListener("portal-link-refresh", onRefresh); };
  }, []);

  if (loading || !data) {
    return (
      <div className="rounded-xl border border-border p-6 text-foreground/50 text-[13px] flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin"/>
        <span>Loading your launch kit…</span>
      </div>
    );
  }

  const { url, brand_name, primary_color, qr_png_b64 } = data;
  const banner = htmlBannerSnippet({ url, brandName: brand_name, primaryColor: primary_color });
  const widget = floatingWidgetSnippet({ url, brandName: brand_name, primaryColor: primary_color });
  const signature = emailSignatureSnippet({ url, brandName: brand_name, primaryColor: primary_color });

  const downloadQr = () => {
    const a = document.createElement("a");
    a.href = `data:image/png;base64,${qr_png_b64}`;
    a.download = `${(brand_name || "portal").toLowerCase().replace(/\s+/g, "-")}-portal-qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  return (
    <div className="space-y-4" data-testid="launch-kit-panel">
      {/* Tab switcher */}
      <div className="flex flex-wrap gap-2 p-1 rounded-full bg-secondary/50 w-fit border border-border">
        <LaunchKitTab icon={Globe}             label="Direct link"      active={tab === "link"}      onClick={() => setTab("link")}      testId="launch-tab-link"/>
        <LaunchKitTab icon={Code}              label="HTML banner"      active={tab === "banner"}    onClick={() => setTab("banner")}    testId="launch-tab-banner"/>
        <LaunchKitTab icon={MousePointerClick} label="Floating widget"  active={tab === "widget"}    onClick={() => setTab("widget")}    testId="launch-tab-widget"/>
        <LaunchKitTab icon={Mail}              label="Email signature"  active={tab === "signature"} onClick={() => setTab("signature")} testId="launch-tab-signature"/>
      </div>

      {tab === "link" && (
        <div className="rounded-xl border border-border p-5 space-y-4" data-testid="launch-kit-link">
          <div>
            <div className="text-[12px] mono uppercase tracking-widest text-foreground/55 mb-2">Your portal URL</div>
            <div className="flex flex-wrap items-center gap-2">
              <code className="mono text-[13.5px] flex-1 min-w-0 truncate rounded-lg bg-secondary/60 px-3 py-2 border border-border">{url}</code>
              <CopyBlock text={url} testId="copy-portal-url" label="Copy URL"/>
              <a href={url} target="_blank" rel="noreferrer">
                <Button size="sm" variant="outline" className="rounded-full" data-testid="open-portal-url">
                  <ExternalLink className="w-3.5 h-3.5"/>
                  <span className="ml-1.5">Open</span>
                </Button>
              </a>
            </div>
            <div className="text-[11.5px] text-foreground/55 mt-2 flex items-center gap-1.5">
              <Sparkles className="w-3 h-3"/>
              Uses your {
                data.kind === "custom_domain"      ? "custom domain" :
                data.kind === "branded_subdomain"  ? "branded subdomain" :
                data.kind === "account_subdomain"  ? "account URL" :
                data.kind === "auto_subdomain"     ? "verqo subdomain" :
                                                     "platform portal page"
              } automatically.
            </div>

            {data.pending_custom_domain ? (
              <div
                className="mt-3 rounded-lg border border-amber-500/30 bg-amber-50/60 px-3 py-2.5 flex flex-wrap items-center gap-2"
                data-testid="pending-custom-domain"
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-700 shrink-0"/>
                <div className="text-[11.5px] text-amber-900 leading-snug flex-1 min-w-0">
                  <span className="font-semibold">Pending DNS verification: </span>
                  Once active, your portal URL will become{" "}
                  <code className="mono text-[11px] bg-white/70 px-1.5 py-0.5 rounded border border-amber-500/30 break-all">
                    {data.pending_custom_domain.url}
                  </code>
                </div>
              </div>
            ) : null}
          </div>

          <div className="border-t border-border pt-4 flex flex-wrap items-start gap-5">
            <div className="flex flex-col items-center gap-2">
              <img
                src={`data:image/png;base64,${qr_png_b64}`}
                alt="Portal QR code"
                className="w-32 h-32 rounded-lg border border-border bg-white p-2"
                data-testid="portal-qr-img"
              />
              <Button size="sm" variant="outline" onClick={downloadQr} className="rounded-full" data-testid="download-qr">
                <Download className="w-3.5 h-3.5"/>
                <span className="ml-1.5">Download PNG</span>
              </Button>
            </div>
            <div className="flex-1 min-w-[240px] text-[13px] text-foreground/70">
              <div className="font-medium text-foreground mb-1 flex items-center gap-1.5">
                <QrCode className="w-4 h-4"/>
                Scan-to-buy QR
              </div>
              Print this on trade-show booths, pack-slips, business cards, line-sheet PDFs — anywhere a buyer might want to start an order on their phone. They'll land directly in your branded portal.
            </div>
          </div>
        </div>
      )}

      {tab === "banner" && (
        <div className="rounded-xl border border-border p-5 space-y-4" data-testid="launch-kit-banner">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[14px] font-medium text-foreground flex items-center gap-1.5"><Code className="w-4 h-4"/>HTML banner snippet</div>
              <div className="text-[12.5px] text-foreground/60 mt-1">Paste into your Shopify / Webflow / WordPress site as a custom HTML block. Self-styled — drops in any layout.</div>
            </div>
            <CopyBlock text={banner} testId="copy-banner-snippet" label="Copy HTML"/>
          </div>
          <div>
            <div className="text-[11px] mono uppercase tracking-widest text-foreground/55 mb-2">Preview</div>
            <div className="rounded-lg p-5 bg-secondary/30 border border-dashed border-border">
              <div dangerouslySetInnerHTML={{ __html: banner }}/>
            </div>
          </div>
          <pre className="rounded-lg bg-foreground/95 text-background/95 p-4 text-[11.5px] mono leading-snug overflow-auto max-h-72" data-testid="banner-source">{banner}</pre>
        </div>
      )}

      {tab === "widget" && (
        <div className="rounded-xl border border-border p-5 space-y-4" data-testid="launch-kit-widget">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[14px] font-medium text-foreground flex items-center gap-1.5"><MousePointerClick className="w-4 h-4"/>Floating button widget</div>
              <div className="text-[12.5px] text-foreground/60 mt-1">Paste before <code className="mono text-[11px]">&lt;/body&gt;</code> on your site. Renders a pill in the bottom-right corner that hovers above your content.</div>
            </div>
            <CopyBlock text={widget} testId="copy-widget-snippet" label="Copy HTML"/>
          </div>
          <div>
            <div className="text-[11px] mono uppercase tracking-widest text-foreground/55 mb-2">Preview</div>
            <div className="rounded-lg bg-secondary/30 border border-dashed border-border h-52 relative overflow-hidden">
              <div className="absolute right-5 bottom-5">
                <div style={{
                  background: primary_color, color: "#fff",
                  padding: "14px 22px", borderRadius: 999, fontFamily: "Inter,sans-serif",
                  fontSize: 14, fontWeight: 600, boxShadow: "0 12px 28px rgba(0,0,0,0.18)",
                  display: "flex", alignItems: "center", gap: 10,
                }}>
                  <span style={{ fontSize: 16, lineHeight: 1 }}>📦</span>
                  <span>Wholesale</span>
                </div>
              </div>
              <div className="absolute inset-0 grid place-items-center text-foreground/30 text-[11px] mono uppercase tracking-widest">Your site content</div>
            </div>
          </div>
          <pre className="rounded-lg bg-foreground/95 text-background/95 p-4 text-[11.5px] mono leading-snug overflow-auto max-h-72" data-testid="widget-source">{widget}</pre>
        </div>
      )}

      {tab === "signature" && (
        <div className="rounded-xl border border-border p-5 space-y-4" data-testid="launch-kit-signature">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[14px] font-medium text-foreground flex items-center gap-1.5"><Mail className="w-4 h-4"/>Email signature block</div>
              <div className="text-[12.5px] text-foreground/60 mt-1">In Gmail / Outlook signature editor, paste this as <em>formatted</em> content (not source). Every reply turns into a wholesale touch-point.</div>
            </div>
            <CopyBlock text={signature} testId="copy-signature-snippet" label="Copy HTML"/>
          </div>
          <div>
            <div className="text-[11px] mono uppercase tracking-widest text-foreground/55 mb-2">Preview</div>
            <div className="rounded-lg p-5 bg-secondary/30 border border-dashed border-border">
              <div dangerouslySetInnerHTML={{ __html: signature }}/>
            </div>
          </div>
          <pre className="rounded-lg bg-foreground/95 text-background/95 p-4 text-[11.5px] mono leading-snug overflow-auto max-h-72" data-testid="signature-source">{signature}</pre>
        </div>
      )}

      {/* Invite CTA — cross-link to existing /seller/buyers flow */}
      <div className="rounded-xl border border-border p-4 flex flex-wrap items-center justify-between gap-3 bg-accent/10" data-testid="invite-buyers-cta">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-foreground text-background grid place-items-center">
            <Send className="w-4 h-4"/>
          </div>
          <div>
            <div className="text-[13.5px] font-medium text-foreground">Want to invite buyers directly?</div>
            <div className="text-[12px] text-foreground/65">Send a single invite or paste up to 200 emails — buyers get an email + bell notification with a one-click accept link.</div>
          </div>
        </div>
        <a href="/seller/buyers">
          <Button size="sm" className="rounded-full bg-foreground text-background" data-testid="invite-buyers-link">
            Go to Buyer invites
            <ExternalLink className="w-3.5 h-3.5 ml-1.5"/>
          </Button>
        </a>
      </div>
    </div>
  );
}


// ── Domain ─────────────────────────────────────────────────────────────

// Unified Auto-configure wizard — ONE button → detects seller's DNS
// provider → routes to the right flow:
//   • DomainConnect-supported (GoDaddy, IONOS, Squarespace, …) → popup
//   • Cloudflare-managed → token-paste sub-wizard
//   • Anything else → manual CNAME instructions
function AutoConfigureWizard({ open, onClose, domain, onSuccess, onShowManual }) {
  // Master state machine:
  //   detecting → (decides) → dc-ready | cf-friendly | manual
  //   cf-friendly → (advanced) → cf-token → cf-running → done | error
  //   dc-ready    → (popup opens) → dc-waiting → (callback marks tenant) → done
  const [step, setStep] = useState("detecting");
  const [probe, setProbe] = useState(null);      // result of /detect-provider
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // CF-path state
  const [apiToken, setApiToken] = useState("");
  const [zoneId, setZoneId] = useState("");
  const [verifyState, setVerifyState] = useState(null);
  const [result, setResult] = useState(null);

  // DC-path state
  const [popupRef, setPopupRef] = useState(null);

  // Detect provider as soon as the modal opens.
  useEffect(() => {
    if (!open) return;
    setStep("detecting"); setProbe(null); setError("");
    setApiToken(""); setZoneId(""); setVerifyState(null); setResult(null);
    (async () => {
      try {
        const { data } = await api.get(`/tenant/domain/detect-provider`, { params: { domain } });
        setProbe(data);
        // Route to the appropriate sub-flow
        if (data.auto_configure_path === "domainconnect")   setStep("dc-ready");
        else if (data.auto_configure_path === "cloudflare") setStep("cf-friendly");
        else                                                 setStep("manual");
      } catch (e) {
        setError(e?.response?.data?.detail || "Detection failed. Defaulting to manual setup.");
        setStep("manual");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, domain]);

  // ── DomainConnect: launch the provider popup ────────────────────────
  const startDomainConnect = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/tenant/domain/domainconnect/start", { domain });
      const p = window.open(data.popup_url, "verqo-dc", "width=720,height=820,popup");
      setPopupRef(p);
      setStep("dc-waiting");
      // Poll the seller's tenant doc to detect when the callback fires.
      const startedAt = Date.now();
      const pollId = setInterval(async () => {
        try {
          const { data: status } = await api.get("/tenant/domain/status");
          const ac = status?.auto_configure;
          if (ac?.completed_at) {
            clearInterval(pollId);
            if (ac.dns_success) {
              setStep("done"); setResult({ ...data, dns_action: "created" });
              onSuccess?.(status);
            } else {
              setStep("error"); setError(ac.dns_error || "Provider didn't approve the record");
            }
          }
          if (Date.now() - startedAt > 5 * 60 * 1000) {
            clearInterval(pollId);
            setStep("error");
            setError("Timed out waiting for your DNS provider — did the popup get blocked?");
          }
        } catch { /* keep polling */ }
      }, 2000);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Failed to start DomainConnect");
      setStep("error");
    } finally { setBusy(false); }
  };

  // ── Cloudflare token path (existing flow) ──────────────────────────
  const verifyCfToken = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/tenant/domain/verify-dns-token", {
        api_token: apiToken.trim(), domain,
      });
      setVerifyState(data);
      if (data.zone_id) setZoneId(data.zone_id);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Token verification failed";
      setError(typeof msg === "string" ? msg : (msg.message || "Failed"));
      setVerifyState({ ok: false });
    } finally { setBusy(false); }
  };
  const runCfWrite = async () => {
    setStep("cf-running"); setBusy(true); setError("");
    try {
      const { data } = await api.post("/tenant/domain/auto-configure", {
        domain, provider: "cloudflare",
        cf_api_token: apiToken.trim(),
        cf_zone_id:   zoneId.trim() || undefined,
      });
      setResult(data);
      if (data.ok) { setStep("done"); onSuccess?.(data); }
      else { setError(data.error || "Auto-configure didn't complete."); setStep("error"); }
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Auto-configure failed";
      setError(typeof msg === "string" ? msg : (msg.message || "Failed"));
      setStep("error");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { try { popupRef?.close?.(); } catch {} onClose?.(); } }}>
      <DialogContent className="max-w-lg" data-testid="auto-configure-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[17px]">
            <Sparkles className="w-4 h-4 text-accent"/>
            Auto-configure {domain}
          </DialogTitle>
          <DialogDescription className="text-[12.5px] text-foreground/65">
            One click. Verqo detects your DNS provider and writes the record on your behalf. Buyers see {domain} only — no verqo branding.
          </DialogDescription>
        </DialogHeader>

        {/* ── DETECTING (initial probe) ───────────────────────────── */}
        {step === "detecting" && (
          <div className="py-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-3"/>
            <div className="text-[14px] font-medium">Detecting your DNS provider…</div>
            <div className="mt-1 text-[12px] text-foreground/55">Reading the public DNS records for {domain.split(".").slice(-2).join(".")}</div>
          </div>
        )}

        {/* ── DC PATH: ready to launch the popup ──────────────────── */}
        {step === "dc-ready" && probe && (
          <div className="space-y-4 mt-2">
            <div className="rounded-xl border-2 border-emerald-500/30 bg-emerald-50/30 p-4">
              <div className="flex items-start gap-3">
                <Check className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5"/>
                <div>
                  <div className="text-[14px] font-semibold text-emerald-900">
                    Found your DNS provider — <span className="font-mono">{probe.provider_label}</span>
                  </div>
                  <p className="mt-1.5 text-[12.5px] text-emerald-800/85">
                    {probe.provider_label} supports one-click setup. Clicking <strong>Configure</strong> opens
                    their secure sign-in page in a new window. After you approve, we write the DNS record
                    automatically and your portal goes live in 1-3 minutes.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={startDomainConnect} disabled={busy} className="bg-foreground text-background" data-testid="dc-launch">
                {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Starting…</>
                       : <><ExternalLink className="w-4 h-4 mr-1.5"/>Configure via {probe.provider_label}</>}
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* ── DC PATH: popup open, waiting for callback ───────────── */}
        {step === "dc-waiting" && (
          <div className="py-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-3"/>
            <div className="text-[14px] font-medium">Waiting for {probe?.provider_label || "your provider"}…</div>
            <div className="mt-1 text-[12px] text-foreground/55 max-w-sm mx-auto">
              Approve the request in the popup that just opened. We'll detect it the moment the record is written.
              <br/>(If you don't see the popup, your browser may have blocked it — close this and try again with popups allowed.)
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="mt-4 text-foreground/50">Cancel</Button>
          </div>
        )}

        {/* ── CF PATH: friendly redirect to manual (Cloudflare does not
            support third-party DNS writes without a customer-issued API
            token, so the cleanest UX is to show the seller exactly which
            CNAME to add inside their own dashboard). The "Advanced" link
            below still exposes the token-paste flow for power users. */}
        {step === "cf-friendly" && probe && (
          <div className="space-y-4 mt-2">
            <div className="rounded-xl border border-border bg-secondary/30 p-4">
              <div className="text-[13.5px] mb-2">
                <Check className="w-3.5 h-3.5 inline text-emerald-600 mr-1"/>
                Detected your DNS is on <span className="font-mono font-semibold">Cloudflare</span>.
              </div>
              <p className="text-[12.5px] text-foreground/70 leading-relaxed">
                Cloudflare doesn't let third-party apps write DNS records into your zone without
                a customer-issued API token. The cleanest path is to add one CNAME inside your
                Cloudflare dashboard yourself — it takes about <strong>60 seconds</strong>.
              </p>
              <p className="mt-2 text-[12px] text-foreground/55">
                We'll show you the exact record + step-by-step screenshots for Cloudflare.
              </p>
            </div>
            <div className="rounded-lg border border-foreground/10 bg-white p-3">
              <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/55 mb-2">The record you'll add</div>
              <div className="grid grid-cols-3 gap-2 text-[12.5px]">
                <div>
                  <div className="text-[10.5px] uppercase text-foreground/45 mb-1">Type</div>
                  <div className="font-mono bg-foreground/5 px-2 py-1 rounded">CNAME</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase text-foreground/45 mb-1">Name</div>
                  <div className="font-mono bg-foreground/5 px-2 py-1 rounded">{domain ? domain.split(".")[0] : "shop"}</div>
                </div>
                <div>
                  <div className="text-[10.5px] uppercase text-foreground/45 mb-1">Target</div>
                  <div className="font-mono bg-foreground/5 px-2 py-1 rounded">cname.verqo.com</div>
                </div>
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} data-testid="cf-friendly-cancel">Cancel</Button>
              <Button onClick={() => { onShowManual?.(); onClose?.(); }}
                      className="bg-foreground text-background"
                      data-testid="cf-friendly-show-manual">
                <Sparkles className="w-4 h-4 mr-1.5"/>Show Cloudflare steps
              </Button>
            </DialogFooter>
            <div className="pt-1 text-center">
              <button type="button"
                      className="text-[11px] text-foreground/50 underline underline-offset-2 hover:text-foreground/80"
                      onClick={() => setStep("cf-token")}
                      data-testid="cf-friendly-advanced">
                Advanced: paste a Cloudflare API token instead
              </button>
            </div>
          </div>
        )}

        {/* ── CF PATH: token paste (when seller's domain is on CF) ── */}
        {step === "cf-token" && probe && (
          <div className="space-y-4 mt-2">
            <div className="rounded-xl border border-border bg-secondary/30 p-4">
              <div className="text-[13.5px] mb-2">
                <Check className="w-3.5 h-3.5 inline text-emerald-600 mr-1"/>
                Detected your DNS is on <span className="font-mono font-semibold">Cloudflare</span>.
              </div>
              <p className="text-[12px] text-foreground/65">
                Paste a Cloudflare API token scoped to <span className="font-mono">{probe.root_domain}</span> and we'll write the CNAME automatically.
              </p>
              <ol className="mt-2 text-[12px] text-foreground/65 list-decimal pl-5 space-y-1">
                <li>Go to <a href="https://dash.cloudflare.com/profile/api-tokens" target="_blank" rel="noreferrer" className="text-accent underline underline-offset-2">dash.cloudflare.com/profile/api-tokens</a></li>
                <li>Click <strong>Create Token</strong> → template <strong>"Edit zone DNS"</strong></li>
                <li>Restrict it to just <span className="font-mono">{probe.root_domain}</span></li>
                <li>Paste the token below</li>
              </ol>
            </div>
            <Input
              type="password" value={apiToken}
              onChange={(e) => { setApiToken(e.target.value); setVerifyState(null); }}
              placeholder="Paste your Cloudflare API token"
              className="font-mono text-[12.5px]"
              data-testid="auto-config-token"
            />
            {verifyState?.ok && (
              <div className="flex items-start gap-2 text-[12.5px] text-emerald-700">
                <Check className="w-3.5 h-3.5 mt-0.5"/>
                <div>
                  Token verified.{verifyState.zone_id ? "" : " (Token valid, but no zone found for this domain — check the token has access to it.)"}
                </div>
              </div>
            )}
            {error && (<div className="flex items-start gap-2 text-[12px] text-rose-700"><AlertCircle className="w-3.5 h-3.5 mt-0.5"/><div>{error}</div></div>)}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              {!verifyState?.ok ? (
                <Button onClick={verifyCfToken} disabled={!apiToken || busy} data-testid="auto-config-verify">
                  {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Verifying…</> : "Verify token"}
                </Button>
              ) : (
                <Button onClick={runCfWrite} disabled={busy || !verifyState.zone_id} className="bg-foreground text-background" data-testid="auto-config-run">
                  {busy ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin"/>Configuring…</> : <><Sparkles className="w-4 h-4 mr-1.5"/>Configure DNS now</>}
                </Button>
              )}
            </DialogFooter>
          </div>
        )}

        {step === "cf-running" && (
          <div className="py-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-3"/>
            <div className="text-[14px] font-medium">Writing your DNS record…</div>
            <div className="mt-1 text-[12px] text-foreground/55">Usually takes 5-10 seconds.</div>
          </div>
        )}

        {/* ── MANUAL FALLBACK (provider not supported by either auto path) */}
        {step === "manual" && probe && (
          <div className="space-y-4 mt-2">
            <div className="rounded-xl border border-amber-300 bg-amber-50/50 p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5"/>
                <div>
                  <div className="text-[14px] font-semibold text-amber-900">
                    Manual setup needed
                  </div>
                  <p className="mt-1.5 text-[12.5px] text-amber-900/85">
                    Your DNS provider ({probe.provider_label}) doesn't support one-click setup yet.
                    You'll need to add one CNAME record manually — it takes about 60 seconds. Close this and use the
                    {" "}<strong>Save (manual)</strong> button on the main form — we'll show you the exact record + screenshots.
                  </p>
                </div>
              </div>
            </div>
            <DialogFooter><Button onClick={onClose}>Got it</Button></DialogFooter>
          </div>
        )}

        {/* ── SUCCESS ────────────────────────────────────────────── */}
        {step === "done" && (
          <div className="py-2">
            <div className="rounded-2xl border-2 border-emerald-500/30 bg-emerald-50/40 p-5">
              <div className="flex items-start gap-3">
                <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-500/15 grid place-items-center">
                  <Check className="w-5 h-5 text-emerald-700"/>
                </div>
                <div>
                  <div className="text-[15px] font-semibold text-emerald-900">DNS record written ✓</div>
                  <div className="mt-1.5 text-[12.5px] text-emerald-800/85">
                    Cloudflare is validating ownership + provisioning the SSL cert.
                    Your portal will be live at <code className="font-mono">{domain}</code> in <strong>1-3 minutes</strong>.
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4"><Button onClick={onClose} className="bg-foreground text-background">Done</Button></DialogFooter>
          </div>
        )}

        {/* ── ERROR ──────────────────────────────────────────────── */}
        {step === "error" && (
          <div className="py-2">
            <div className="rounded-2xl border-2 border-rose-300 bg-rose-50/40 p-5">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5"/>
                <div>
                  <div className="text-[14px] font-semibold text-rose-900">Couldn't complete auto-configure</div>
                  <div className="mt-1.5 text-[12.5px] text-rose-800/85">{error || "Something went wrong"}</div>
                  <div className="mt-3 text-[11.5px] text-foreground/55">
                    Close this and use the <strong>Save (manual)</strong> button to set up the CNAME yourself.
                  </div>
                </div>
              </div>
            </div>
            <DialogFooter className="mt-4 gap-2">
              <Button variant="outline" onClick={onClose}>Close</Button>
              <Button onClick={() => setStep(probe?.auto_configure_path === "domainconnect" ? "dc-ready" : probe?.auto_configure_path === "cloudflare" ? "cf-friendly" : "manual")}>Try again</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


// ══════════════════════════════════════════════════════════════════════════
// Domain — three-card layout (Feb 2026 redesign)
//
//   Option 1 · Account URL       → tenant.settings.auto_subdomain (permanent)
//   Option 2 · Branded subdomain → tenant.settings.branded_subdomain (opt-in)
//   Option 3 · Custom domain     → tenant.settings.custom_domain (white-label)
//
// The seller picks ONE as primary via tenant.settings.primary_url_kind.
// All three URLs stay reachable — primary is what appears in invites,
// invoices, launch-kit assets, and storefront redirects.
// ══════════════════════════════════════════════════════════════════════════
const SUGGESTED_SUBDOMAINS = ["shop", "wholesale", "store", "portal", "buy"];

function _validateBrandedSlug(s) {
  const v = (s || "").trim().toLowerCase();
  if (!v) return { ok: false, err: "Pick a subdomain." };
  if (!/^[a-z0-9](?:[a-z0-9-]{0,46}[a-z0-9])?$/.test(v)) {
    return { ok: false, err: "Use 2-48 chars: letters, numbers, hyphens only." };
  }
  const RESERVED = ["www","accounts","buyers","sellers","admin","ops","api","app","mail","email","support","blog","docs","status","help","shop","cname","cdn","static","assets","media","files","secure","login","signup","register","auth","oauth","sso","ftp","smtp","ns","ns1","ns2","mx","webhook","webhooks","verqo","traqo","dev","staging","prod","test"];
  if (RESERVED.includes(v)) return { ok: false, err: `"${v}" is reserved.` };
  return { ok: true };
}


function OptionCard({ kind, label, description, url, status, isPrimary, onSelect, badge, points, configUi, primaryBlockedReason }) {
  const tones = {
    primary: { border: "border-2 border-emerald-500", bg: "bg-emerald-50/40" },
    ready:   { border: "border border-foreground/15",  bg: "bg-white" },
    setup:   { border: "border-2 border-dashed border-foreground/15", bg: "bg-foreground/[0.02]" },
    pending: { border: "border-2 border-amber-300",   bg: "bg-amber-50/40" },
  };
  const t = tones[status] || tones.ready;
  const Icon = kind === "account" ? Hash : kind === "branded" ? Sparkles : Globe;
  return (
    <div className={`relative rounded-2xl ${t.border} ${t.bg} p-5 transition-all flex flex-col`} data-testid={`option-${kind}`}>
      <div className="absolute top-3 right-3">
        {isPrimary ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-emerald-500 text-white" data-testid={`primary-pill-${kind}`}>
            <Crown className="w-3 h-3"/> Primary
          </span>
        ) : badge ? (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-medium uppercase tracking-wider bg-foreground/10 text-foreground/65">{badge}</span>
        ) : null}
      </div>
      <div className="flex items-start gap-3 mb-3 pr-24">
        <div className="shrink-0 w-9 h-9 rounded-lg bg-foreground/5 grid place-items-center">
          <Icon className="w-4 h-4"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10.5px] mono uppercase tracking-[0.2em] text-foreground/55">{label}</div>
          {url ? (
            <div className="mt-1 font-mono text-[14px] text-foreground/85 break-all leading-snug">{url}</div>
          ) : (
            <div className="mt-1 text-[13.5px] text-foreground/40 italic">Not configured</div>
          )}
        </div>
      </div>
      <p className="text-[12px] text-foreground/65 leading-relaxed mb-3">{description}</p>
      {points && (
        <ul className="space-y-1 mb-4">
          {points.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-[11.5px]">
              {p.ok
                ? <Check className="w-3.5 h-3.5 text-emerald-600 mt-0.5 shrink-0"/>
                : <span className="w-3.5 h-3.5 mt-0.5 shrink-0 text-foreground/30 grid place-items-center text-[12px]">x</span>}
              <span className={p.ok ? "text-foreground/80" : "text-foreground/45"}>{p.label}</span>
            </li>
          ))}
        </ul>
      )}
      {configUi && (
        <div className="mt-1 mb-4 pt-3 border-t border-foreground/10">
          {configUi}
        </div>
      )}
      <div className="mt-auto flex flex-wrap gap-2">
        {url && status === "primary" && (
          <Button size="sm" variant="outline" className="rounded-full" onClick={() => window.open(`https://${url}`, "_blank")} data-testid={`test-${kind}`}>
            <ExternalLink className="w-3 h-3 mr-1.5"/>Test storefront
          </Button>
        )}
        {/* Iter 211 · Option 3 (custom domain) needs to be selectable
            BEFORE any URL is entered, so the email-domain-first flow
            can trigger. For the other options (which always have a
            URL available), keep the original gate. */}
        {(url || kind === "custom") && !isPrimary && (status !== "setup" || kind === "custom") && (
          primaryBlockedReason ? (
            <div className="w-full">
              <Button size="sm" disabled className="rounded-full bg-foreground/15 text-foreground/50 cursor-not-allowed" data-testid={`make-primary-${kind}-blocked`}>
                <Crown className="w-3 h-3 mr-1.5"/>Make primary
              </Button>
              <div className="mt-1.5 text-[10.5px] text-amber-700 leading-snug">
                {primaryBlockedReason}
              </div>
            </div>
          ) : (
            <Button size="sm" className="rounded-full bg-foreground text-background" onClick={onSelect} data-testid={`make-primary-${kind}`}>
              <Crown className="w-3 h-3 mr-1.5"/>Make primary
            </Button>
          )
        )}
        {url && (
          <Button size="sm" variant="ghost" className="rounded-full text-foreground/60"
                  onClick={() => { navigator.clipboard.writeText(`https://${url}`); toast.success("Copied"); }}>
            <Copy className="w-3 h-3 mr-1.5"/>Copy
          </Button>
        )}
      </div>
    </div>
  );
}


function ManualDnsAccordion({ host = "shop", target = "cname.verqo.com", open: openProp, onOpenChange, provider: providerProp, onProviderChange }) {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp !== undefined ? openProp : openInternal;
  const setOpen = (v) => {
    if (onOpenChange) onOpenChange(v);
    else setOpenInternal(v);
  };
  const [providerInternal, setProviderInternal] = useState("godaddy");
  const provider = providerProp !== undefined ? providerProp : providerInternal;
  const setProvider = (v) => {
    if (onProviderChange) onProviderChange(v);
    else setProviderInternal(v);
  };
  const PROVIDERS = [
    { id: "godaddy",    label: "GoDaddy" },
    { id: "namecheap",  label: "Namecheap" },
    { id: "cloudflare", label: "Cloudflare" },
    { id: "route53",    label: "AWS Route 53" },
    { id: "google",     label: "Google Domains" },
    { id: "other",      label: "Other" },
  ];
  const STEPS = {
    godaddy: [
      "Sign in to GoDaddy -> click your username top-right -> My Products.",
      "Find your domain -> click the green DNS button.",
      "Under Records, click Add -> choose Type: CNAME.",
      `Name: enter just the subdomain (e.g. ${host}) - NOT the full domain.`,
      `Value: ${target}`,
      "TTL: 1 Hour. Click Save.",
    ],
    namecheap: [
      "Sign in to Namecheap -> Domain List -> click MANAGE next to your domain.",
      "Click the Advanced DNS tab.",
      "Click ADD NEW RECORD -> choose Type: CNAME Record.",
      `Host: ${host}. Value: ${target}`,
      "TTL: Automatic. Click the green checkmark to save.",
    ],
    cloudflare: [
      "Sign in to dash.cloudflare.com -> click your domain.",
      "Click DNS -> Records.",
      `Click Add record -> Type: CNAME. Name: ${host}. Target: ${target}`,
      "Proxy status: DNS only (grey cloud) - IMPORTANT.",
      "TTL: Auto. Click Save.",
    ],
    route53: [
      "AWS Console -> Route 53 -> Hosted zones -> click your domain.",
      "Click Create record -> Routing policy: Simple routing.",
      `Record name: ${host}. Type: CNAME. Value: ${target}`,
      "TTL: 300 sec. Save.",
    ],
    google: [
      "domains.google.com -> click Manage on your domain.",
      "DNS -> Custom records -> Manage custom records.",
      `Create new record -> Host: ${host}. Type: CNAME. Data: ${target}`,
      "TTL: 1H. Save.",
    ],
    other: [
      "Find your provider's DNS management (sometimes Zone editor / DNS Records).",
      "Add new record -> Type: CNAME (or Alias / Pointer record).",
      `Host / Name / Source: ${host}`,
      `Points to / Value / Target: ${target}`,
      "Leave TTL default. Save.",
    ],
  };
  return (
    <div className="rounded-xl border border-foreground/10 bg-foreground/[0.02] overflow-hidden">
      <button onClick={() => setOpen(o => !o)} className="w-full px-5 py-4 flex items-center justify-between gap-3 hover:bg-foreground/[0.03] transition-colors" data-testid="dns-accordion-toggle">
        <div className="flex items-center gap-3 text-left">
          <Globe className="w-4 h-4 text-foreground/55"/>
          <div>
            <div className="text-[13.5px] font-medium">Need to add the DNS record manually?</div>
            <div className="text-[11.5px] text-foreground/55">Step-by-step for the most common DNS providers.</div>
          </div>
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-4">
          <div className="flex flex-wrap gap-1.5 pt-3">
            {PROVIDERS.map(p => {
              const active = p.id === provider;
              return (
                <button key={p.id} onClick={() => setProvider(p.id)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-all ${active ? "bg-foreground text-background border-foreground" : "bg-white text-foreground/70 border-foreground/15 hover:border-foreground/35"}`}>
                  {p.label}
                </button>
              );
            })}
          </div>
          <div className="rounded-lg border border-foreground/10 bg-white p-4">
            <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/55 mb-2">The record to add</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-[12.5px]">
              <div><div className="text-[10.5px] uppercase text-foreground/45 mb-1">Type</div><div className="font-mono bg-foreground/5 px-2 py-1 rounded">CNAME</div></div>
              <div><div className="text-[10.5px] uppercase text-foreground/45 mb-1">Host / Name</div><div className="font-mono bg-foreground/5 px-2 py-1 rounded">{host}</div></div>
              <div><div className="text-[10.5px] uppercase text-foreground/45 mb-1">Points to</div><div className="font-mono bg-foreground/5 px-2 py-1 rounded">{target}</div></div>
            </div>
          </div>
          <div className="rounded-lg border border-foreground/10 bg-white p-4">
            <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/55 mb-3">
              Steps for {PROVIDERS.find(p => p.id === provider)?.label}
            </div>
            <ol className="space-y-2 text-[12.5px] text-foreground/80">
              {STEPS[provider].map((step, i) => (
                <li key={i} className="flex gap-3">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-foreground text-background grid place-items-center text-[10.5px] font-semibold">{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}


export function DomainPanel() {
  const [tenant, setTenant] = useState(null);
  const [accountUrl, setAccountUrl] = useState(null);
  const [brandedUrl, setBrandedUrl] = useState("");
  const [customDomain, setCustomDomain] = useState("");
  const [customStatus, setCustomStatus] = useState(null);
  // Iter 211 · Email sending domain status lifted from EmailDomainCard
  // so we can gate the "Save custom domain" buttons + swap red-flag
  // banners when the seller has selected Option 3 but hasn't verified
  // their email domain yet.
  const [emailDomain, setEmailDomain] = useState({ id: null, name: null, status: null });
  const emailDomainVerified = emailDomain.status === "verified";
  const [primaryKind, setPrimaryKind] = useState("account");
  const [working, setWorking] = useState(false);
  const [autoConfigOpen, setAutoConfigOpen] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  const [brandedInput, setBrandedInput] = useState("");
  const [customInput, setCustomInput] = useState("");
  // Controls the manual-DNS accordion at the bottom + lets us programmatically
  // open + scroll-to it when the seller hits "Save (manual)".
  const [manualDnsOpen, setManualDnsOpen] = useState(false);
  const [manualDnsProvider, setManualDnsProvider] = useState("godaddy");
  // Phase IV-Domain-Fix — Cloudflare for SaaS surfaces TWO validation
  // records on hostname creation (SSL cert TXT + ownership TXT). If the
  // seller's customer-side domain is ALSO on Cloudflare, BOTH TXT
  // records must be added on the customer's DNS or Cloudflare's edge
  // emits Error 1014 (CNAME Cross-User Banned). We pull these from the
  // tenant settings so they can be shown alongside the manual CNAME
  // instructions instead of being hidden inside the Cloudflare API.
  const [cfValidation, setCfValidation] = useState(null);
  const [cfLastError, setCfLastError] = useState(null);
  const [reregistering, setReregistering] = useState(false);

  const load = async () => {
    const { data } = await api.get("/tenant/me");
    setTenant(data);
    const s = data?.settings || {};
    setAccountUrl(s.account_subdomain || s.auto_subdomain || null);
    setBrandedUrl(s.branded_subdomain || s.auto_subdomain || "");
    setCustomDomain(s.custom_domain || "");
    setCustomStatus(s.custom_domain_status || null);
    setPrimaryKind(s.primary_url_kind || "account");
    setCfValidation(s.custom_domain_validation || null);
    setCfLastError(s.custom_domain_last_error || null);
  };
  useEffect(() => { load(); }, []);

  // Re-register the Cloudflare Custom Hostname for the current domain.
  // Used when the original save errored out (silently or otherwise) and
  // the tenant is stuck without a cf_hostname_id. Backend tries lookup
  // first, then create, so this is safe to call multiple times.
  const cfReregister = async () => {
    setReregistering(true);
    try {
      const { data } = await api.post("/tenant/domain/cf-reregister");
      setCfValidation(data?.validation || null);
      setCfLastError(null);
      setCustomStatus(data?.status || customStatus);
      toast.success("Re-registered with Cloudflare. Check the TXT + CNAME records below.");
      window.dispatchEvent(new Event("portal-link-refresh"));
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || e?.response?.data?.detail || "Re-register failed";
      setCfLastError(msg);
      toast.error(msg);
    } finally { setReregistering(false); }
  };

  useEffect(() => {
    if (brandedUrl) setBrandedInput(brandedUrl.replace(/\.verqo\.com$/, ""));
    if (customDomain) setCustomInput(customDomain);
  }, [brandedUrl, customDomain]);

  useEffect(() => {
    if (!customDomain) return undefined;
    if (customStatus === "active" || customStatus === "failed") return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/tenant/domain/status");
        if (!alive) return;
        const next = data?.status || null;
        if (next === "active" && customStatus !== "active") {
          window.dispatchEvent(new Event("portal-link-refresh"));
        }
        setCustomStatus(next);
      } catch { /* swallow */ }
    };
    void tick();
    const id = setInterval(tick, 10000);
    return () => { alive = false; clearInterval(id); };
  }, [customDomain, customStatus, pollTick]);

  const ensureAccountUrl = async () => {
    if (accountUrl) return;
    setWorking(true);
    try {
      const { data } = await api.post("/tenant/domain/generate-subdomain", {});
      setAccountUrl(data.subdomain);
      window.dispatchEvent(new Event("portal-link-refresh"));
      toast.success("Account URL provisioned");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't provision account URL");
    } finally { setWorking(false); }
  };

  const saveBranded = async () => {
    const v = _validateBrandedSlug(brandedInput);
    if (!v.ok) { toast.error(v.err); return; }
    setWorking(true);
    try {
      const { data } = await api.post("/tenant/domain/branded-subdomain", { slug: brandedInput.trim().toLowerCase() });
      setBrandedUrl(data.branded_subdomain);
      window.dispatchEvent(new Event("portal-link-refresh"));
      toast.success("Branded subdomain saved");
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not save branded subdomain"));
    } finally { setWorking(false); }
  };
  const clearBranded = async () => {
    if (!window.confirm(`Remove ${brandedUrl}? Buyers using this URL will be redirected to your primary URL.`)) return;
    setWorking(true);
    try {
      await api.delete("/tenant/domain/branded-subdomain");
      setBrandedUrl("");
      setBrandedInput("");
      if (primaryKind === "branded") setPrimaryKind("account");
      toast.success("Branded subdomain removed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setWorking(false); }
  };

  const makePrimary = async (kind) => {
    if (kind === primaryKind) return;
    setWorking(true);
    try {
      await api.post("/tenant/domain/set-primary", { kind });
      setPrimaryKind(kind);
      window.dispatchEvent(new Event("portal-link-refresh"));
      // When setting custom as primary BEFORE DNS verifies, the backend
      // resolver gracefully falls back to the account URL until activation.
      // Use a longer "info" toast (8s instead of 4s) so the seller doesn't
      // miss the explainer — this is exactly the kind of UX moment where
      // a generic "saved" toast would leave them confused later.
      if (kind === "custom" && customStatus !== "active") {
        toast.info(
          "Custom domain set as primary. While DNS verifies, buyers see your account URL — automatic switch once active.",
          { duration: 8000 },
        );
      } else {
        toast.success("Primary URL updated");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Could not update primary"));
    } finally { setWorking(false); }
  };

  const saveCustomDomainManual = async () => {
    if (!customInput || !customInput.includes(".")) {
      toast.error("Enter a valid domain like shop.yourbrand.com");
      return;
    }
    setWorking(true);
    try {
      const { data } = await api.post("/tenant/domain/custom", { domain: customInput.trim().toLowerCase() });
      setCustomDomain(customInput.trim().toLowerCase());
      setCustomStatus(data?.status || "pending_dns_verification");
      setPollTick(t => t + 1);
      window.dispatchEvent(new Event("portal-link-refresh"));
      if (data?.error) {
        // CF API failures are common in preview/dev (missing or invalid CLOUDFLARE_API_TOKEN).
        // The tenant save still succeeded — the only thing missing is the automated cert
        // provisioning. We soften the toast so users don't panic.
        const isAuthErr = /401|authentication/i.test(String(data.error));
        toast.warning(isAuthErr
          ? "Saved — automatic SSL provisioning unavailable in this environment. Follow the manual DNS steps below."
          : `Saved — Cloudflare returned: ${data.error}`);
      }
      else             toast.success("Custom domain saved - follow the DNS steps below");
      // Open + scroll to the manual-DNS section so the seller can step
      // through the provider instructions immediately.
      setManualDnsOpen(true);
      setTimeout(() => {
        const el = document.getElementById("manual-dns-section");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save domain");
    } finally { setWorking(false); }
  };

  const disconnectCustom = async () => {
    if (!window.confirm(`Disconnect ${customDomain}? Buyers will fall back to your other URLs.`)) return;
    setWorking(true);
    try {
      await api.delete("/tenant/domain/custom");
      setCustomDomain("");
      setCustomStatus(null);
      if (primaryKind === "custom") setPrimaryKind("account");
      window.dispatchEvent(new Event("portal-link-refresh"));
      toast.success("Custom domain disconnected");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not disconnect");
    } finally { setWorking(false); }
  };

  const companyRoot = (() => {
    if (customDomain) {
      const parts = customDomain.split(".");
      return parts.slice(-2).join(".");
    }
    if (customInput && customInput.includes(".")) {
      const parts = customInput.split(".");
      return parts.slice(-2).join(".");
    }
    return "yourbrand.com";
  })();
  const customSuggestions = SUGGESTED_SUBDOMAINS.map(sub => `${sub}.${companyRoot}`);
  // `primaryUrl` is the seller's INTENT (the URL of the option flagged
  // Primary). This is what shows in the OptionCard's "Primary" pill.
  const primaryUrl =
    primaryKind === "branded" ? brandedUrl :
    primaryKind === "custom"  ? customDomain :
    accountUrl;

  // `operationalUrl` is what buyers actually land on RIGHT NOW. Mirrors
  // the backend resolver `_seller_portal_url_sync()` so this section
  // stays in lock-step with the launch-kit's `/seller/portal-link` result.
  // Without this, the two sections can diverge — the banner shows the
  // intent (e.g. app.brnddirect.com) while the launch kit shows the
  // fallback (e.g. glowco.verqo.com), which is confusing for sellers.
  const operationalUrl = (() => {
    // 1. Honor explicit primary IF its URL is active/available
    if (primaryKind === "custom"  && customDomain && customStatus === "active") return customDomain;
    if (primaryKind === "branded" && brandedUrl)                                return brandedUrl;
    if (primaryKind === "account" && accountUrl)                                return accountUrl;
    // 2. Legacy default: an active custom domain always wins
    if (customDomain && customStatus === "active") return customDomain;
    // 3. Fallback chain: branded → account
    return brandedUrl || accountUrl;
  })();
  const pendingPrimary = primaryUrl && operationalUrl && primaryUrl !== operationalUrl
    ? primaryUrl : null;
  const customHostPrefix = customDomain ? customDomain.split(".")[0] : "shop";

  return (
    <div className="space-y-8">
      <div>
        <div className="mb-2 text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/45">Domain</div>
        <h1 className="text-[22px] md:text-[26px] font-semibold tracking-tight">Where buyers shop</h1>
        <p className="mt-2 text-[13.5px] text-foreground/65 max-w-2xl leading-relaxed">
          Choose how buyers reach your wholesale portal. You can offer up to three URLs at the same time -
          pick one as <strong>primary</strong> to be the URL we use in invites, invoices, and storefront links.
        </p>
      </div>

      {/* Account ID strip — informational, always present. This is the seller's
       *  stable portal identifier — used internally for banking, credit,
       *  insurance, and admin reference. Distinct from the storefront URL
       *  options below (which can change). Buyers never see this ID directly. */}
      {(tenant?.settings?.account_subdomain_short_id || tenant?.settings?.account_subdomain) && (
        <div className="rounded-2xl border border-foreground/10 bg-white p-5 flex flex-wrap items-center gap-4" data-testid="account-id-strip">
          <div className="shrink-0 w-11 h-11 rounded-xl bg-foreground/5 grid place-items-center">
            <Hash className="w-5 h-5 text-foreground/55"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/55">Your verqo account ID</div>
            <div className="mt-1 font-mono text-[15px] text-foreground/85 truncate">
              {tenant.settings.account_subdomain_short_id
                || (tenant.settings.account_subdomain || "").replace(/\.verqo\.com$/, "")}
            </div>
            <div className="mt-1 text-[11.5px] text-foreground/55">
              Used internally for banking, credit, insurance, and admin reference - buyers don't see this.
            </div>
          </div>
          <Button size="sm" variant="outline" className="rounded-full"
                  onClick={() => {
                    const id = tenant.settings.account_subdomain_short_id
                      || (tenant.settings.account_subdomain || "").replace(/\.verqo\.com$/, "");
                    navigator.clipboard.writeText(id);
                    toast.success("Account ID copied");
                  }}
                  data-testid="account-id-copy">
            <Copy className="w-3 h-3 mr-1.5"/>Copy
          </Button>
        </div>
      )}

      <div>
        <div className="flex items-end justify-between mb-4 flex-wrap gap-2">
          <div>
            <h2 className="text-[16px] font-semibold tracking-tight">Storefront URL options</h2>
            <p className="text-[12px] text-foreground/60 mt-1">Pick one as primary. The primary is what buyers see + what gets used in invites.</p>
          </div>
          {operationalUrl && (
            <div className="text-[11px] text-foreground/55 text-right">
              Currently primary: <span className="mono text-foreground/85">{operationalUrl}</span>
              {pendingPrimary && (
                <div className="mt-0.5 text-[10px] text-amber-700">
                  <Clock className="w-2.5 h-2.5 inline mr-0.5"/>
                  Switches to <span className="mono">{pendingPrimary}</span> when DNS activates
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <OptionCard
            kind="account"
            label="Option 1 · Account URL"
            description="Your permanent account-style URL. Works instantly, no setup, never changes - but not branded."
            url={accountUrl}
            status={primaryKind === "account" ? "primary" : (accountUrl ? "ready" : "setup")}
            isPrimary={primaryKind === "account"}
            onSelect={() => makePrimary("account")}
            points={[
              { ok: true,  label: "Active instantly - no DNS work" },
              { ok: true,  label: "Permanent - references stay stable" },
              { ok: false, label: "Not branded - shows account ID" },
            ]}
            configUi={!accountUrl && (
              <Button size="sm" className="rounded-full bg-foreground text-background"
                      disabled={working} onClick={ensureAccountUrl}>
                <Sparkles className="w-3 h-3 mr-1.5"/>Provision account URL
              </Button>
            )}
          />

          <OptionCard
            kind="branded"
            label="Option 2 · Branded subdomain"
            description="A cleaner verqo subdomain using your shop name. Looks polished but has 'verqo' in the URL."
            url={brandedUrl}
            status={primaryKind === "branded" ? "primary" : (brandedUrl ? "ready" : "setup")}
            isPrimary={primaryKind === "branded"}
            onSelect={() => makePrimary("branded")}
            points={[
              { ok: true,  label: "Active instantly - no DNS work" },
              { ok: true,  label: "Cleaner branded URL" },
              { ok: false, label: "Has \"verqo\" in the URL" },
            ]}
            configUi={
              <div className="space-y-2">
                <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/55">
                  {brandedUrl ? "Change the subdomain" : "Pick a subdomain"}
                </div>
                <div className="flex items-center gap-1.5 rounded-lg border border-foreground/15 bg-white px-2 focus-within:border-foreground/40">
                  <Input value={brandedInput}
                          onChange={(e) => setBrandedInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                          placeholder="your-shop"
                          className="border-0 px-1 h-9 font-mono text-[13px] focus-visible:ring-0"
                          data-testid="branded-input"/>
                  <span className="text-[12px] mono text-foreground/55 shrink-0 pr-1">.verqo.com</span>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {SUGGESTED_SUBDOMAINS.map(s => (
                    <button key={s} type="button" onClick={() => setBrandedInput(s)}
                            className={`px-2 py-0.5 rounded-full text-[10px] mono border ${
                              brandedInput === s ? "bg-foreground text-background border-foreground"
                                : "bg-white text-foreground/65 border-foreground/15 hover:border-foreground/35"
                            }`}>
                      {s}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-full flex-1"
                          disabled={working || !brandedInput || brandedInput === brandedUrl?.replace(/\.verqo\.com$/, "")}
                          onClick={saveBranded} data-testid="branded-save">
                    {brandedUrl ? "Update" : "Save"}
                  </Button>
                  {brandedUrl && (
                    <Button size="sm" variant="ghost" className="rounded-full text-foreground/55 hover:text-rose-700"
                            onClick={clearBranded} disabled={working}>
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            }
          />

          <OptionCard
            kind="custom"
            label="Option 3 · Your own domain"
            description="Fully white-label. Your domain, your brand, no verqo branding visible to buyers."
            url={customDomain || null}
            status={
              primaryKind === "custom"            ? "primary" :
              customStatus === "active"           ? "ready"   :
              customDomain                        ? "pending" : "setup"
            }
            isPrimary={primaryKind === "custom"}
            onSelect={() => makePrimary("custom")}
            badge={
              customStatus === "active"                  ? "ACTIVE" :
              customStatus === "pending_dns_verification" ? "DNS PENDING" :
              customStatus === "pending_validation"       ? "VALIDATING" :
              customStatus === "failed"                   ? "FAILED" :
              customStatus ? customStatus.replace(/_/g, " ").toUpperCase() : null
            }
            points={[
              { ok: true,  label: "Fully white-labeled - no verqo branding" },
              { ok: true,  label: "Your brand - your domain" },
              { ok: false, label: "Requires DNS setup (one-click or manual)" },
            ]}
            configUi={
              <div className="space-y-2.5">
                {/* Iter 211 · Email sending domain step — only visible
                    once the seller has clicked "Make primary" on this
                    Option 3 card. Placed ABOVE the storefront input so
                    the flow reads top-down: verify email → set site. */}
                {primaryKind === "custom" && (
                  <div className="pb-3 border-b border-foreground/10">
                    <EmailDomainCard
                      customDomain={customDomain || customInput}
                      onStatusChange={setEmailDomain}
                    />
                  </div>
                )}

                {/* Iter 211 · Red-flag banner blocking save until the
                    email domain is verified. Only shows once Option 3
                    is primary AND email domain hasn't hit "verified". */}
                {primaryKind === "custom" && !emailDomainVerified && (
                  <div
                    data-testid="custom-domain-email-block"
                    className="rounded-lg border border-rose-300 bg-rose-50 text-rose-900 px-3 py-2 text-[11.5px] flex items-start gap-2"
                  >
                    <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
                    <div>
                      <div className="font-semibold">Verify your email domain before saving.</div>
                      <div className="text-rose-800/85 mt-0.5">
                        Buyers can&apos;t see your brand on the site while emails still come from Verqo.
                        Complete the Email sending domain step above — the Save button below unlocks
                        once its status flips to <span className="mono">Verified</span>.
                      </div>
                    </div>
                  </div>
                )}

                <div className="text-[10.5px] mono uppercase tracking-wider text-foreground/55">
                  {customDomain ? "Your domain" : "Enter your domain"}
                </div>
                <Input value={customInput}
                        onChange={(e) => setCustomInput(e.target.value.toLowerCase().trim())}
                        placeholder="shop.yourbrand.com"
                        className="h-9 font-mono text-[13px]"
                        data-testid="custom-input"/>
                {!customDomain && (
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-wider text-foreground/45">Suggested</div>
                    <div className="flex flex-wrap gap-1.5">
                      {customSuggestions.map(s => (
                        <button key={s} type="button" onClick={() => setCustomInput(s)}
                                className={`px-2 py-0.5 rounded-full text-[10px] mono border ${
                                  customInput === s ? "bg-foreground text-background border-foreground"
                                    : "bg-white text-foreground/65 border-foreground/15 hover:border-foreground/35"
                                }`}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <Button size="sm" className="rounded-full w-full bg-foreground text-background hover:bg-foreground/85"
                        disabled={working || !customInput || (primaryKind === "custom" && !emailDomainVerified)}
                        onClick={() => { setAutoConfigOpen(true); }}
                        data-testid="custom-auto-configure">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5"/>Auto-configure
                </Button>
                <div className="flex items-center gap-2 text-[10.5px] text-foreground/55">
                  <Check className="w-3 h-3"/>
                  <span>
                    We'll detect <span className="font-mono">{customInput ? customInput.split(".").slice(-2).join(".") : "your-domain.com"}</span> hosting and write DNS automatically.
                  </span>
                </div>
                <div className="text-center text-[10.5px] text-foreground/45 pt-1">- or -</div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-full flex-1"
                          disabled={working || !customInput || (primaryKind === "custom" && !emailDomainVerified)}
                          onClick={saveCustomDomainManual} data-testid="custom-save-manual">
                    {customDomain ? "Update (manual)" : "Save (manual)"}
                  </Button>
                  {customDomain && (
                    <Button size="sm" variant="ghost" className="rounded-full text-foreground/55 hover:text-rose-700"
                            onClick={disconnectCustom} disabled={working} data-testid="custom-disconnect">
                      Remove
                    </Button>
                  )}
                </div>
                {/* Phase IV-Domain-Fix — surface the Cloudflare last_error
                 *  AND a one-click "Re-register" button when our backend
                 *  failed to register the Custom Hostname (401, rate limit,
                 *  etc.). Without this the seller is stuck at "DNS PENDING"
                 *  forever with no feedback. */}
                {customDomain && cfLastError && (
                  <div className="rounded-lg border border-rose-300 bg-rose-50/60 p-3 text-[11.5px] text-rose-900 leading-snug space-y-2" data-testid="custom-cf-error">
                    <div className="flex items-start gap-1.5">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5"/>
                      <div>
                        <div className="font-medium mb-0.5">Cloudflare registration didn't complete</div>
                        <div className="break-words">{cfLastError}</div>
                      </div>
                    </div>
                    <Button size="sm" className="rounded-full bg-foreground text-background h-7 text-[11.5px]"
                            disabled={reregistering} onClick={cfReregister}
                            data-testid="custom-cf-reregister">
                      <RefreshCw className={`w-3 h-3 mr-1.5 ${reregistering ? "animate-spin" : ""}`}/>
                      {reregistering ? "Re-registering…" : "Retry Cloudflare registration"}
                    </Button>
                  </div>
                )}
              </div>
            }
          />
        </div>
      </div>

      {operationalUrl && (
        <div className="rounded-2xl border-2 border-emerald-500/25 bg-emerald-50/30 p-5" data-testid="primary-url-strip">
          <div className="flex flex-wrap items-center gap-4 justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-emerald-700 shrink-0"/>
              <div>
                <div className="text-[13px] font-semibold text-emerald-900">
                  Primary URL - buyers will land here
                </div>
                <div className="mt-0.5 font-mono text-[13.5px] text-emerald-900 break-all">
                  https://{operationalUrl}
                </div>
                {/* When the seller has picked a primary that isn't yet
                 *  operational (e.g. custom domain with pending DNS), surface
                 *  the future-state URL explicitly. Both Sections A and B
                 *  show the same operational URL above, eliminating the
                 *  "which one is real?" confusion that sellers reported. */}
                {pendingPrimary && (
                  <div className="mt-1.5 inline-flex items-center gap-1.5 text-[11.5px] text-amber-800">
                    <Clock className="w-3 h-3"/>
                    Pending · once <span className="font-mono">{pendingPrimary}</span> activates, that becomes the primary URL. The system auto-switches — no action needed.
                  </div>
                )}
              </div>
            </div>
            <Button size="sm" variant="outline" className="rounded-full bg-white"
                    onClick={() => window.open(`https://${operationalUrl}`, "_blank")}>
              <ExternalLink className="w-3 h-3 mr-1.5"/>Test storefront
            </Button>
          </div>
        </div>
      )}

      <div>
        <div className="mb-3">
          <h2 className="text-[16px] font-semibold tracking-tight">Promote your portal</h2>
          <p className="text-[12px] text-foreground/60 mt-1 max-w-2xl leading-relaxed">
            Drop-in assets to send your existing buyers into your wholesale portal - share the link, a QR code,
            an embeddable HTML banner, a floating "Order wholesale" button widget, or an email signature block.
          </p>
        </div>
        <PortalLaunchKitPanel/>
      </div>

      {/* Manual DNS instructions — always visible so sellers can preview the
       *  setup steps before committing a custom domain. The host hint
       *  defaults to "shop" until a real custom domain is saved. Wrapped in
       *  an id so saveCustomDomainManual can scroll the user here. */}
      {/* Phase IV-Domain-Fix — Cloudflare for SaaS additional TXT records.
       *  When the customer's domain is ALSO on Cloudflare, Cloudflare blocks
       *  the cross-account CNAME with Error 1014 unless BOTH the SSL TXT
       *  AND the hostname-ownership TXT are added on the customer DNS. We
       *  surface both here so the seller can copy them and add to their
       *  DNS provider. Without this, sellers get stuck at 1014 forever. */}
      {(cfValidation?.ownership?.value || (cfValidation?.ssl_records?.length || 0) > 0) && (
        <div className="rounded-xl border border-amber-300 bg-amber-50/40 p-5 space-y-3" data-testid="cf-validation-records">
          <div className="flex items-start gap-2.5">
            <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5"/>
            <div className="min-w-0">
              <div className="text-[13.5px] font-semibold text-amber-900">
                Add these TXT records on your DNS too
              </div>
              <div className="text-[11.5px] text-amber-900/80 mt-0.5 leading-snug">
                Required by Cloudflare to validate your custom domain. Without these
                you'll see <span className="font-mono">Error 1014</span> when visiting your
                custom domain (even though the CNAME is correct).
              </div>
            </div>
          </div>
          {cfValidation?.ownership?.value && (
            <CfTxtRecordCard
              label="Hostname ownership"
              type="TXT"
              name={cfValidation.ownership.name}
              value={cfValidation.ownership.value}
              testid="cf-ownership-txt"
            />
          )}
          {(cfValidation?.ssl_records || []).map((rec, i) => (
            <CfTxtRecordCard
              key={i}
              label="SSL certificate validation"
              type={(rec.txt_name ? "TXT" : rec.type || "TXT").toUpperCase()}
              name={rec.txt_name || rec.name}
              value={rec.txt_value || rec.value}
              testid={`cf-ssl-txt-${i}`}
            />
          ))}
        </div>
      )}

      <div id="manual-dns-section">
        <ManualDnsAccordion
          host={customHostPrefix}
          target="cname.verqo.com"
          open={manualDnsOpen}
          onOpenChange={setManualDnsOpen}
          provider={manualDnsProvider}
          onProviderChange={setManualDnsProvider}
        />
      </div>

      <AutoConfigureWizard
        open={autoConfigOpen}
        onClose={() => setAutoConfigOpen(false)}
        domain={customInput}
        onShowManual={() => {
          // Triggered when the seller is on Cloudflare and chooses to
          // add the CNAME themselves. We preselect the Cloudflare tab in
          // the manual-DNS accordion, open it, and scroll the seller to
          // it so the next action is obvious.
          setManualDnsProvider("cloudflare");
          setManualDnsOpen(true);
          setTimeout(() => {
            const el = document.getElementById("manual-dns-section");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 250);
        }}
        onSuccess={async () => {
          setAutoConfigOpen(false);
          setCustomDomain(customInput.trim().toLowerCase());
          setPollTick(t => t + 1);
          await load();
        }}
      />
    </div>
  );
}



// ── Operations ─────────────────────────────────────────────────────────
// Per-seller defaults that pre-fill order-flow dialogs. Currently just the
// production lead time (used to pre-fill the "Set ready date" dialog), but
// this is the natural place for fulfillment-style defaults to land later.
const LEAD_OPTIONS = [1, 2, 3, 5, 7, 10, 14, 21, 30];

export function OperationsPanel() {
  const [tenant, setTenant] = useState(null);
  const [leadDays, setLeadDays] = useState("");
  const [qiDays, setQiDays] = useState("");
  const [pickupWindow, setPickupWindow] = useState("");
  const [termsStartEvent, setTermsStartEvent] = useState("ship_date");
  // Phase IV — invoice validity window. Default 30 days, range 1–365.
  // When an issued invoice isn't accepted (paid OR authorized) before this
  // window elapses, the platform auto-voids it and the seller can re-issue
  // at current pricing. Buyers see the countdown on their invoice rows.
  const [invoiceValidity, setInvoiceValidity] = useState("30");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tenant/me").then(r => {
      setTenant(r.data);
      const cur = r.data?.settings?.default_lead_days;
      setLeadDays(cur != null ? String(cur) : "");
      const qi = r.data?.settings?.qi_window_days;
      setQiDays(qi != null ? String(qi) : "");
      const pw = r.data?.settings?.default_pickup_window;
      setPickupWindow(pw || "");
      const iv = r.data?.settings?.invoice_validity_days;
      setInvoiceValidity(iv != null ? String(iv) : "30");
    });
    api.get("/seller/settings/credit-terms")
      .then(r => setTermsStartEvent(r.data?.terms_start_event || "ship_date"))
      .catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      // Note: `default_lead_days` is intentionally NOT in this payload —
      // lead time is buyer-facing and lives in Public profile (single
      // source of truth, Jun 2026 IA cleanup).
      const qi = qiDays === "" ? null : Number(qiDays);
      if (qi != null && (!Number.isFinite(qi) || qi < 1 || qi > 60)) {
        toast.error("QI deadline must be a whole number between 1 and 60 days");
        return;
      }
      const iv = invoiceValidity === "" ? null : Number(invoiceValidity);
      if (iv != null && (!Number.isFinite(iv) || iv < 1 || iv > 365)) {
        toast.error("Invoice validity must be between 1 and 365 days");
        return;
      }
      await Promise.all([
        api.patch("/tenant/me", {
          settings: {
            qi_window_days: qi == null ? null : Math.round(qi),
            default_pickup_window: pickupWindow.trim() || null,
            invoice_validity_days: iv == null ? null : Math.round(iv),
          },
        }),
        api.patch("/seller/settings/credit-terms", { terms_start_event: termsStartEvent }),
      ]);
      toast.success("Operations defaults saved.");
    } finally { setSaving(false); }
  };

  if (!tenant) return <Loader2 className="w-4 h-4 animate-spin text-foreground/40"/>;

  return (
    <div data-testid="ops-panel">
      <SectionHeader
        title="Operations"
        desc="Internal defaults that pre-fill your order workflow so you don't repeat the same numbers on every PO. Buyer-facing details (lead time, MOQ, shipping) live in Public profile."
      />
      {/* Lead time lives in Public profile (single source of truth — it's
          a buyer-facing field they see before ordering). Show a cross-ref
          so sellers don't go hunting. */}
      <FieldRow label="Default lead time">
        <div className="text-[12px] text-foreground/55 rounded-lg border border-foreground/10 bg-secondary/30 px-3 py-2 inline-flex items-center gap-2"
             data-testid="ops-leadtime-ref">
          <Clock className="w-3 h-3"/>
          Lead time lives in <a href="#public-profile" onClick={(e) => { e.preventDefault(); window.location.hash = "public-profile"; }} className="underline hover:text-foreground">Public profile</a> — buyers see it on your storefront.
        </div>
      </FieldRow>
      <FieldRow
        label="Quality-inspection deadline"
        hint="How long buyers have after delivery to open a Claim (damage, shortage, qty mismatch). After this window expires, claims auto-close. Default 7 days. Set per your warehouse policy — most sellers use 5–10 days."
      >
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={1}
            max={60}
            step={1}
            value={qiDays}
            placeholder="7"
            onChange={e => setQiDays(e.target.value)}
            data-testid="ops-qi-days"
            className="h-10 w-24 text-sm"
          />
          <span className="text-xs text-foreground/55">
            {qiDays
              ? `Buyers can open a claim within ${qiDays} day${Number(qiDays) === 1 ? "" : "s"} of delivery.`
              : "Using default — 7 days after delivery."}
          </span>
        </div>
      </FieldRow>
      <FieldRow
        label="Default pickup window"
        hint="Pre-fills the 'Pickup window' field every time you mark an order ready for pickup. Free-text — describe the hours and days carriers can collect at your default warehouse."
      >
        <div className="flex items-center gap-2 w-full max-w-md">
          <Input
            value={pickupWindow}
            placeholder="Mon–Fri 9am–5pm ET"
            onChange={e => setPickupWindow(e.target.value)}
            data-testid="ops-pickup-window"
            className="h-10 text-sm"
          />
        </div>
      </FieldRow>

      {/* Phase IV — Invoice validity window.
       *  How long an issued invoice stays valid for the buyer to pay or
       *  authorize. After this window expires, the invoice auto-voids and
       *  the seller can re-issue at current pricing. The buyer sees a
       *  live countdown on their invoice rows + this value on the seller's
       *  public terms so there are no surprises. Default 30 days. */}
      <FieldRow
        label="Invoice validity"
        hint="How many days an issued invoice stays open for the buyer to pay or authorize. After this window, the invoice auto-voids and you can re-issue at the current price. Default 30 days. Buyers see the countdown on their invoice and this value on your public terms."
      >
        <div className="flex items-center gap-2 max-w-md">
          <Input
            type="number"
            min={1}
            max={365}
            step={1}
            value={invoiceValidity}
            placeholder="30"
            onChange={e => setInvoiceValidity(e.target.value)}
            data-testid="ops-invoice-validity-days"
            className="h-10 w-28 text-sm"
          />
          <span className="text-xs text-foreground/55">
            {invoiceValidity
              ? `Invoices auto-void after ${invoiceValidity} day${Number(invoiceValidity) === 1 ? "" : "s"} if the buyer hasn't paid or authorized.`
              : "Using default — 30 days."}
          </span>
        </div>
      </FieldRow>

      <FieldRow
        label="Credit-terms clock start"
        hint="When the Net-X due-date clock starts on your seller-direct credit offers. Lender-funded buyer credit and your factored invoices always anchor to carrier shipment (Allianz cover requires it)."
      >
        <div className="space-y-2" data-testid="ops-terms-start-event">
          {[
            { v: "ship_date",     label: "Ship date",
              sub: "Clock starts when carrier confirms goods are in transit. Recommended — what every modern B2B platform uses." },
            { v: "invoice_date",  label: "Invoice date",
              sub: "Clock starts the moment the invoice is issued. Legacy convention." },
            { v: "delivery_date", label: "Delivery date (Net-X ROG)",
              sub: "Clock starts when the buyer signs for delivery. Most buyer-friendly." },
          ].map(opt => (
            <label key={opt.v}
              data-testid={`ops-terms-start-${opt.v}`}
              className={`flex items-start gap-3 rounded-xl border px-3.5 py-3 cursor-pointer transition-colors ${
                termsStartEvent === opt.v
                  ? "border-foreground bg-secondary/40"
                  : "border-border hover:bg-secondary/20"
              }`}>
              <input
                type="radio"
                name="terms-start-event"
                value={opt.v}
                checked={termsStartEvent === opt.v}
                onChange={() => setTermsStartEvent(opt.v)}
                className="mt-1"
              />
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  {opt.label}
                  {opt.v === "ship_date" && <span className="chip bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px]">Recommended</span>}
                </div>
                <div className="text-[12px] text-foreground/55 mt-0.5 leading-relaxed">{opt.sub}</div>
              </div>
            </label>
          ))}
        </div>
      </FieldRow>
      <div className="mt-4">
        <Button onClick={save} disabled={saving} data-testid="ops-save"
          className="rounded-full bg-foreground text-background hover:bg-foreground/90">
          {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Saving…</> : "Save defaults"}
        </Button>
      </div>

      {/* Test Mode toggle — Shopify Bogus Gateway pattern */}
      <TestModeCard />
    </div>
  );
}


// ── Auto-payout schedule (seller-only) ─────────────────────────────────
export function PayoutSchedulePanel() {
  const [s, setS] = useState(null);
  const [methods, setMethods] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/seller/payout-schedule").then(r => setS(r.data));
    api.get("/payment-methods").then(r => setMethods(r.data || [])).catch(() => setMethods([]));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/seller/payout-schedule", {
        enabled: !!s.enabled,
        frequency: s.frequency,
        day_of_week: Number(s.day_of_week ?? 1),
        day_of_month: Number(s.day_of_month ?? 1),
        minimum_balance: Number(s.minimum_balance ?? 0),
        destination_method_id: s.destination_method_id || null,
      });
      setS(data);
      toast.success(data.enabled ? "Auto-payout schedule active" : "Auto-payout disabled");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <Loader2 className="w-4 h-4 animate-spin text-foreground/40"/>;

  return (
    <div data-testid="payout-schedule-panel">
      <SectionHeader title="Auto-payout schedule" desc="Set & forget — your wallet pays out to your bank automatically on a schedule. Manual payouts always remain available."/>
      <div className="space-y-3 max-w-xl">
        <label className="flex items-center gap-3">
          <input type="checkbox" checked={!!s.enabled} onChange={e => setS({...s, enabled: e.target.checked})} data-testid="payout-enabled"/>
          <span className="text-sm">Enable scheduled auto-payout</span>
        </label>
        <FieldRow label="Frequency">
          <select value={s.frequency} onChange={e => setS({...s, frequency: e.target.value})}
            className="h-10 rounded-md border border-foreground/15 bg-white px-3 text-sm pr-8" data-testid="payout-frequency">
            <option value="daily">Daily (every business day)</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </FieldRow>
        {s.frequency === "weekly" && (
          <FieldRow label="Day of week">
            <select value={s.day_of_week ?? 1} onChange={e => setS({...s, day_of_week: Number(e.target.value)})}
              className="h-10 rounded-md border border-foreground/15 bg-white px-3 text-sm pr-8">
              {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((n,i) => <option key={i} value={i}>{n}</option>)}
            </select>
          </FieldRow>
        )}
        {s.frequency === "monthly" && (
          <FieldRow label="Day of month">
            <Input type="number" min={1} max={28} value={s.day_of_month ?? 1}
              onChange={e => setS({...s, day_of_month: Number(e.target.value)})} className="w-24"/>
          </FieldRow>
        )}
        <FieldRow label="Minimum balance" hint="Skip the payout if the wallet balance is below this number after platform-fee/reserve deductions.">
          <Input type="number" min={0} value={s.minimum_balance ?? 0}
            onChange={e => setS({...s, minimum_balance: e.target.value})} className="w-32" data-testid="payout-min"/>
        </FieldRow>
        <FieldRow label="Destination bank">
          <select value={s.destination_method_id || ""} onChange={e => setS({...s, destination_method_id: e.target.value || null})}
            className="h-10 rounded-md border border-foreground/15 bg-white px-3 text-sm pr-8 w-full max-w-md" data-testid="payout-dest">
            <option value="">— Default bank on file —</option>
            {methods.filter(m => m.kind === "bank" || m.kind === "ach").map(m => (
              <option key={m.id} value={m.id}>
                {m.label || m.bank_name || "Bank"} ····{m.last4 || m.account_last4 || "—"}
              </option>
            ))}
          </select>
        </FieldRow>
        <div>
          <Button onClick={save} disabled={saving} data-testid="payout-save"
            className="rounded-full bg-foreground text-background hover:bg-foreground/90">
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Saving…</> : <><CalendarClock className="w-3.5 h-3.5 mr-1.5"/>Save schedule</>}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ── 1099-K export (seller, IRS form) ───────────────────────────────────
export function TaxReportsPanel() {
  const [year, setYear] = useState(new Date().getFullYear() - 1);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/seller/1099-k/${year}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `1099k_${year}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`1099-K data for ${year} downloaded`);
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="tax-panel">
      <SectionHeader title="Tax reports" desc="Generate a 1099-K-style CSV summarizing your gross payment volume per buyer for a given calendar year. Hand this to your accountant or import into a filing service."/>
      <div className="rounded-xl border border-border p-4 max-w-xl">
        <div className="text-sm font-medium mb-2">1099-K data export</div>
        <div className="text-[12px] text-foreground/65 mb-3">Per-buyer breakdown of paid invoices. NOT an actual IRS filing — it's the underlying data only.</div>
        <div className="flex items-center gap-2">
          <Input type="number" min={2020} max={new Date().getFullYear()} value={year}
            onChange={e => setYear(Number(e.target.value))} className="w-28" data-testid="tax-year"/>
          <Button onClick={download} disabled={busy} className="rounded-full bg-foreground text-background" data-testid="tax-download">
            {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>…</> : <><Download className="w-3.5 h-3.5 mr-1.5"/>Download CSV</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Public profile (the /s/:handle page sellers manage) ───────────────
// All fields land in tenant.settings.profile. Read-only sub-fields like
// QI window stay in OperationsPanel (single source of truth).
export function PublicProfilePanel() {
  const [tenant, setTenant] = useState(null);
  const [form, setForm] = useState({
    tagline: "", about: "", location: "", founded_year: "", website: "",
    contact_email: "", categories: "", terms_and_conditions: "",
    // Phase A — order economics. Jun 2026 IA cleanup: `lead_days` lives
    // here in Public profile as canonical (buyer-facing). `qi_days` moved
    // to Operations as canonical (internal warehouse policy). The other
    // duplicate display was removed.
    moq: "", warranty: "", lead_days: "",
    // Wholesale return policy (Jun 2026)
    //   "" = unset (storefront shows "Per bilateral agreement")
    //   "0" = final sale, no returns
    //   "N" = N-day return window from delivery
    return_policy_days: "",
    // Phase B — logistics
    ships_from: "", ships_to: "", dropship_eligible: false, offer_seller_arranged_shipping: false,
    restocking_fee_pct: "",
    offers_prebook: false,
    // Phase C — social handles
    social_instagram: "", social_tiktok: "", social_linkedin: "",
    social_x: "", social_facebook: "", social_youtube: "",
    // Phase 2c — Shipping cost passthrough. When the seller books a
    // carrier, the cost (plus optional markup) is auto-added to the
    // buyer's unpaid invoice. Defaults: on, 0% markup, $0 floor.
    passthrough_enabled: true, passthrough_markup_pct: "0", passthrough_min_charge: "0",
  });
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/tenant/me").then(r => {
      const t = r.data; setTenant(t);
      const p = t.settings?.profile || {};
      const s = t.settings || {};
      const soc = p.social || {};
      // Use functional form so we preserve any keys (like passthrough_*)
      // that the parallel `/seller/shipping/passthrough-config` fetch may
      // have already populated. Without this, whichever fetch resolves
      // SECOND wins and the other half of the form gets wiped.
      setForm(prev => ({
        ...prev,
        tagline:       p.tagline || "",
        about:         p.about || "",
        location:      p.location || "",
        founded_year:  p.founded_year || "",
        website:       p.website || "",
        contact_email: p.contact_email || "",
        categories:    Array.isArray(p.categories) ? p.categories.join(", ") : (p.categories || ""),
        terms_and_conditions: p.terms_and_conditions || "",
        moq:           p.moq || "",
        warranty:      p.warranty || "",
        lead_days:     s.default_lead_days != null ? String(s.default_lead_days) : "",
        return_policy_days: p.return_policy_days != null ? String(p.return_policy_days) : "",
        ships_from:    p.ships_from || "",
        ships_to:      p.ships_to || "",
        dropship_eligible: !!p.dropship_eligible,
        offer_seller_arranged_shipping: !!p.offer_seller_arranged_shipping,
        restocking_fee_pct: p.restocking_fee_pct != null ? String(p.restocking_fee_pct) : "",
        offers_prebook: !!p.offers_prebook,
        social_instagram: soc.instagram || "",
        social_tiktok:    soc.tiktok || "",
        social_linkedin:  soc.linkedin || "",
        social_x:         soc.x || "",
        social_facebook:  soc.facebook || "",
        social_youtube:   soc.youtube || "",
      }));
    });
    // Phase 2c — load the tenant's shipping passthrough config (lives at
    // settings.shipping_passthrough, not in profile, so it's a separate fetch).
    api.get("/seller/shipping/passthrough-config").then(r => {
      const c = r.data || {};
      setForm(prev => ({
        ...prev,
        passthrough_enabled:     c.enabled !== false,
        passthrough_markup_pct:  String(c.markup_pct ?? 0),
        passthrough_min_charge:  String(c.min_charge_usd ?? 0),
      }));
    }).catch(() => { /* defaults already in initial state */ });
  }, []);

  const publicUrl = tenant ? `${window.location.origin}/s/${tenant.slug}` : "";

  const save = async () => {
    setSaving(true);
    try {
      const lead_days_n = form.lead_days === "" ? null : Number(form.lead_days);
      if (lead_days_n != null && (!Number.isFinite(lead_days_n) || lead_days_n < 0 || lead_days_n > 365)) {
        toast.error("Lead time must be between 0 and 365 days"); setSaving(false); return;
      }
      // Note: `qi_window_days` is intentionally NOT written here — it
      // lives in Operations (single source of truth, Jun 2026 IA).
      if (form.restocking_fee_pct !== "") {
        const rf = Number(form.restocking_fee_pct);
        if (!Number.isFinite(rf) || rf < 0 || rf > 25) {
          toast.error("Restocking fee must be between 0 and 25%"); setSaving(false); return;
        }
      }
      await api.patch("/tenant/me", {
        settings: {
          // Lead time lives at the top of `settings` (not in `profile`) —
          // already wired into the order workflow there. Public profile
          // owns the canonical write path post-IA cleanup.
          default_lead_days: lead_days_n,
          profile: {
            tagline:       form.tagline || null,
            about:         form.about || null,
            location:      form.location || null,
            founded_year:  form.founded_year || null,
            website:       form.website || null,
            contact_email: form.contact_email || null,
            categories:    form.categories
              ? form.categories.split(",").map(s => s.trim()).filter(Boolean)
              : [],
            terms_and_conditions: form.terms_and_conditions || null,
            moq:                  form.moq || null,
            warranty:             form.warranty || null,
            ships_from:           form.ships_from || null,
            ships_to:             form.ships_to || null,
            dropship_eligible:    !!form.dropship_eligible,
            offer_seller_arranged_shipping: !!form.offer_seller_arranged_shipping,
            restocking_fee_pct:   form.restocking_fee_pct === "" ? null : Number(form.restocking_fee_pct),
            return_policy_days:   form.return_policy_days === "" ? null : Number(form.return_policy_days),
            offers_prebook:       !!form.offers_prebook,
            social: {
              instagram: form.social_instagram || null,
              tiktok:    form.social_tiktok || null,
              linkedin:  form.social_linkedin || null,
              x:         form.social_x || null,
              facebook:  form.social_facebook || null,
              youtube:   form.social_youtube || null,
            },
          },
        },
      });
      // Phase 2c — passthrough config saves to a different endpoint than
      // /tenant/me (it's not inside settings.profile). Best-effort: if the
      // user only edited profile fields, we still hit it to keep the two
      // in sync. Failure here doesn't roll back the profile save.
      const markupN = Number(form.passthrough_markup_pct) || 0;
      const minN    = Number(form.passthrough_min_charge) || 0;
      if (markupN < 0 || markupN > 50) {
        toast.warning("Shipping markup must be 0–50%. Skipped that field.");
      } else if (minN < 0 || minN > 10000) {
        toast.warning("Shipping minimum charge must be $0–$10,000. Skipped that field.");
      } else {
        try {
          await api.patch("/seller/shipping/passthrough-config", {
            enabled:        !!form.passthrough_enabled,
            markup_pct:     markupN,
            min_charge_usd: minN,
          });
        } catch (e) {
          // Non-fatal — profile already saved.
          toast.warning("Shipping passthrough config couldn't save. Try again from this panel.");
        }
      }
      toast.success("Public profile saved.");
      // Notify the portal shell so the sidebar can re-evaluate conditional
      // entries (e.g. the seller-arranges Shipments tab) without a refresh.
      window.dispatchEvent(new Event("tenant-settings-saved"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      toast.success("Shareable link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — your browser blocked clipboard access");
    }
  };

  if (!tenant) return <Loader2 className="w-4 h-4 animate-spin text-foreground/40"/>;

  return (
    <div data-testid="public-profile-panel">
      <FocusScroller/>
      <SectionHeader
        title="Public profile"
        desc="Your buyer-facing storefront. Anything you set here shows up at your shareable link. Includes tagline, lead time, MOQ, shipping policies, warranty, T&Cs. Internal warehouse policies like Quality-inspection window live in Operations."
      />

      {/* Live shareable URL strip */}
      <div className="mb-6 rounded-xl border border-border bg-secondary/40 p-3 flex items-center gap-2 flex-wrap">
        <Globe className="w-4 h-4 text-foreground/55 shrink-0"/>
        <code className="text-[12.5px] text-foreground/85 break-all flex-1 min-w-0">{publicUrl}</code>
        <Button onClick={copyLink} variant="outline" className="rounded-full h-9 px-3.5" data-testid="profile-copy-link">
          {copied ? <><Check className="w-3.5 h-3.5 mr-1.5 text-emerald-600"/>Copied</> : <><Copy className="w-3.5 h-3.5 mr-1.5"/>Copy link</>}
        </Button>
        <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="profile-preview-link">
          <Button variant="outline" className="rounded-full h-9 px-3.5"><Eye className="w-3.5 h-3.5 mr-1.5"/>Preview</Button>
        </a>
      </div>

      <FieldRow label="Tagline" hint="One short line shown under your business name. Max 160 chars.">
        <Input
          value={form.tagline}
          onChange={e => setForm({ ...form, tagline: e.target.value.slice(0, 160) })}
          placeholder="Clean, cruelty-free skincare. Made in California."
          data-testid="profile-tagline"
          className="h-10"
        />
      </FieldRow>

      <FieldRow label="About" hint="2–3 paragraphs. Tell buyers who you are and what makes you different.">
        <Textarea
          value={form.about}
          onChange={e => setForm({ ...form, about: e.target.value.slice(0, 4000) })}
          placeholder="We're a Los Angeles–based wholesale supplier specializing in…"
          data-testid="profile-about"
          rows={5}
        />
      </FieldRow>

      <FieldRow label="Location" hint="City, State or City, Country.">
        <Input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })}
          placeholder="Los Angeles, CA" data-testid="profile-location" className="h-10 max-w-sm"/>
      </FieldRow>

      <FieldRow label="Founded" hint="Year you started.">
        <Input value={form.founded_year} onChange={e => setForm({ ...form, founded_year: e.target.value.slice(0, 10) })}
          placeholder="2019" data-testid="profile-founded" className="h-10 w-32"/>
      </FieldRow>

      <FieldRow label="Website" hint="Your main public website (without https://).">
        <Input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
          placeholder="glowco.com" data-testid="profile-website" className="h-10 max-w-md"/>
      </FieldRow>

      <FieldRow label="Public contact email" hint="Optional. Shown on the profile so buyers can reach you outside verqo.">
        <Input value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
          placeholder="wholesale@glowco.com" type="email" data-testid="profile-contact-email" className="h-10 max-w-md"/>
      </FieldRow>

      <FieldRow label="Categories" hint="Comma-separated tags. Max 12, 40 chars each.">
        <Input value={form.categories} onChange={e => setForm({ ...form, categories: e.target.value })}
          placeholder="Beauty, Skincare, Made in USA, Cruelty-free"
          data-testid="profile-categories" className="h-10"/>
      </FieldRow>

      {/* ── Phase A · Order economics ───────────────────────────────── */}
      <SubSectionHeader title="Order economics" desc="The first questions every wholesale buyer asks. Surfaced as chips on your public profile."/>

      <FieldRow label="Minimum order quantity (MOQ)" hint="Use whatever unit you negotiate in — dollars, units, cases.">
        <Input
          value={form.moq}
          onChange={e => setForm({ ...form, moq: e.target.value.slice(0, 120) })}
          placeholder="$500 or 12 units"
          data-testid="profile-moq"
          className="h-10 max-w-md"
        />
      </FieldRow>

      <FieldRow label="Default lead time" hint="Business days from invoice paid (or Net-X terms accepted) to ready-to-ship. Note: PO acceptance just generates the invoice — the lead-time clock doesn't start until the buyer actually pays or accepts terms. Also pre-fills the 'set ready date' flow on every order.">
        <div className="flex items-center gap-2">
          <Input
            type="number"
            min={0}
            max={365}
            step={1}
            value={form.lead_days}
            onChange={e => setForm({ ...form, lead_days: e.target.value })}
            placeholder="14"
            data-testid="profile-lead-days"
            className="h-10 w-28"
          />
          <span className="text-[12.5px] text-foreground/55">
            {form.lead_days ? `${form.lead_days} business day${Number(form.lead_days) === 1 ? "" : "s"}` : "Leave blank to skip the pre-fill"}
          </span>
        </div>
      </FieldRow>

      <FieldRow label="Quality-inspection (QI) window">
        <div className="text-[12px] text-foreground/55 rounded-lg border border-foreground/10 bg-secondary/30 px-3 py-2 inline-flex items-center gap-2"
             data-testid="profile-qi-ref">
          <Clock className="w-3 h-3"/>
          QI window lives in <a href="#operations" onClick={(e) => { e.preventDefault(); window.location.hash = "operations"; }} className="underline hover:text-foreground">Operations</a> — it's an internal warehouse policy, not a public-storefront field.
        </div>
      </FieldRow>

      <FieldRow label="Warranty" hint="Short policy line — duration + scope. Stamped on every shipped doc.">
        <Input
          value={form.warranty}
          onChange={e => setForm({ ...form, warranty: e.target.value.slice(0, 240) })}
          placeholder="12 months from delivery — defects in materials or workmanship"
          data-testid="profile-warranty"
          className="h-10"
        />
      </FieldRow>

      {/* ── Phase B · Logistics ─────────────────────────────────────── */}
      <SubSectionHeader title="Logistics & geography" desc="Where you ship from, where you ship to, and whether you support drop-shipping."/>

      <FieldRow label="Ships from" hint="City, state, country — buyers use this to estimate freight and customs.">
        <Input
          value={form.ships_from}
          onChange={e => setForm({ ...form, ships_from: e.target.value.slice(0, 120) })}
          placeholder="Los Angeles, CA, USA"
          data-testid="profile-ships-from"
          className="h-10 max-w-md"
        />
      </FieldRow>

      <FieldRow label="Ships to" hint="Continents, countries, or specific regions you fulfill into.">
        <Input
          value={form.ships_to}
          onChange={e => setForm({ ...form, ships_to: e.target.value.slice(0, 240) })}
          placeholder="Continental US · Canada · EU"
          data-testid="profile-ships-to"
          className="h-10"
        />
      </FieldRow>

      <FieldRow id="dropship-eligible" label="Drop-ship eligible" hint="Tick if you fulfill orders directly to your buyers' end-customers (no inventory transfer).">
        <label className="inline-flex items-center gap-2.5 cursor-pointer select-none" data-testid="profile-dropship-toggle">
          <Switch
            checked={form.dropship_eligible}
            onCheckedChange={(v) => setForm({ ...form, dropship_eligible: !!v })}
            data-testid="profile-dropship-switch"
          />
          <span className="text-[13.5px] text-foreground/85">
            {form.dropship_eligible ? "Yes — drop-ship orders accepted" : "No — wholesale orders only"}
          </span>
        </label>
      </FieldRow>

      <FieldRow
        id="offer-seller-arranged-shipping"
        label="Offer seller-arranged shipping"
        hint="By default shipping is the buyer's responsibility (B2B norm). Turn this on if you're happy to arrange shipping on a buyer's behalf — buyers will see a 'Seller arranges' option at PO time. verqo Shipping and buyer-arranged are always offered regardless."
      >
        <label className="inline-flex items-center gap-2.5 cursor-pointer select-none" data-testid="profile-offer-seller-shipping-toggle">
          <Switch
            checked={form.offer_seller_arranged_shipping}
            onCheckedChange={(v) => setForm({ ...form, offer_seller_arranged_shipping: !!v })}
            data-testid="profile-offer-seller-shipping-switch"
          />
          <span className="text-[13.5px] text-foreground/85">
            {form.offer_seller_arranged_shipping
              ? "Yes — I'm open to arranging shipping for buyers"
              : "No — buyers arrange their own shipping"}
          </span>
        </label>
      </FieldRow>

      {/* Phase 2c — Shipping cost passthrough. Only meaningful when the
       *  seller has opted in to arranging shipping above. Hidden when off
       *  to keep the panel tight; backend config is preserved regardless. */}
      {form.offer_seller_arranged_shipping && (
        <FieldRow
          label="Shipping cost passthrough"
          hint="When you book a carrier from your Shipments dashboard, the cost (plus any markup) is added to the buyer's invoice automatically. Markup is capped at 50%."
        >
          <div className="space-y-3" data-testid="shipping-passthrough-config">
            <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
              <Switch
                checked={form.passthrough_enabled}
                onCheckedChange={(v) => setForm({ ...form, passthrough_enabled: !!v })}
                data-testid="passthrough-enabled-switch"
              />
              <span className="text-[13.5px] text-foreground/85">
                {form.passthrough_enabled
                  ? "Auto-bill buyer for shipping cost on each booking"
                  : "Off — I'll absorb shipping costs (or invoice manually)"}
              </span>
            </label>
            {form.passthrough_enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1 max-w-md">
                <label className="block">
                  <span className="text-[10.5px] mono uppercase tracking-widest text-foreground/55">Markup % (handling fee)</span>
                  <div className="flex items-center gap-1.5 mt-1">
                    <Input type="number" min="0" max="50" step="0.5"
                           value={form.passthrough_markup_pct}
                           onChange={(e) => setForm({ ...form, passthrough_markup_pct: e.target.value })}
                           className="font-mono"
                           data-testid="passthrough-markup-pct" />
                    <span className="text-[11.5px] text-foreground/55">%</span>
                  </div>
                </label>
                <label className="block">
                  <span className="text-[10.5px] mono uppercase tracking-widest text-foreground/55">Minimum charge</span>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="text-[12px] text-foreground/55">$</span>
                    <Input type="number" min="0" max="10000" step="0.5"
                           value={form.passthrough_min_charge}
                           onChange={(e) => setForm({ ...form, passthrough_min_charge: e.target.value })}
                           className="font-mono"
                           data-testid="passthrough-min-charge" />
                  </div>
                </label>
              </div>
            )}
            <div className="text-[11px] text-foreground/55 leading-snug">
              Example at <span className="font-mono">{form.passthrough_markup_pct || 0}%</span> markup + <span className="font-mono">${form.passthrough_min_charge || 0}</span> floor on a $12.00 carrier cost:
              {" "}
              <strong className="text-foreground/85">
                ${Math.max(12 * (1 + (Number(form.passthrough_markup_pct) || 0) / 100), Number(form.passthrough_min_charge) || 0).toFixed(2)}
              </strong>{" "}billed to the buyer.
            </div>
          </div>
        </FieldRow>
      )}

      <FieldRow
        label="Cancellation fee (restocking %)"
        hint="Applied when a buyer cancels a PAID order BEFORE it ships, or fails to pick up an order within 30 days. Covers your prep work (picking, packing, allocation). Does NOT apply to post-delivery returns — those are handled separately via your bilateral agreement. Stamped on every invoice + shown on your public profile. Cap: 25%. Leave blank if you don't charge for cancellations."
      >
        <div className="space-y-2 max-w-md">
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min="0" max="25" step="0.5"
              value={form.restocking_fee_pct}
              onChange={e => setForm({ ...form, restocking_fee_pct: e.target.value })}
              placeholder="e.g. 10"
              data-testid="profile-restocking-fee-pct"
              className="h-10 w-28 mono"
            />
            <span className="text-[13.5px] text-foreground/70">% of order total</span>
          </div>
          {/* Live preview of what buyers actually see on the storefront */}
          <div className="text-[11.5px] text-foreground/55 inline-flex items-center gap-1.5 mt-1">
            <Eye className="w-3 h-3"/>
            Buyers will see:{" "}
            <span className="text-foreground/85 font-medium">
              {form.restocking_fee_pct !== "" && Number(form.restocking_fee_pct) > 0
                ? `${form.restocking_fee_pct}% restocking fee on cancelled orders`
                : "No cancellation fee"}
            </span>
          </div>
        </div>
      </FieldRow>

      <FieldRow
        label="Return policy (post-delivery)"
        hint="What happens AFTER the buyer takes delivery — distinct from your cancellation fee above. Pick: (a) Final sale — no returns accepted, (b) Open return window in days, or (c) Leave blank to keep 'Per bilateral agreement' as the storefront fallback. Stamped on every invoice's T&C footer + your public profile."
      >
        <div className="space-y-2.5 max-w-md" data-testid="profile-return-policy-block">
          {/* Three radio modes — UI keeps the state as a string in form.return_policy_days
              ("" = bilateral fallback, "0" = final sale, "N" = window). */}
          <div className="flex items-center gap-1.5 flex-wrap bg-secondary/40 rounded-full p-1 w-fit">
            <button type="button"
              onClick={() => setForm({ ...form, return_policy_days: "" })}
              data-testid="profile-return-mode-bilateral"
              className={`text-sm px-3 py-1.5 rounded-full transition-colors ${form.return_policy_days === "" ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground"}`}
            >
              Per agreement
            </button>
            <button type="button"
              onClick={() => setForm({ ...form, return_policy_days: "0" })}
              data-testid="profile-return-mode-final"
              className={`text-sm px-3 py-1.5 rounded-full transition-colors ${form.return_policy_days === "0" ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground"}`}
            >
              Final sale
            </button>
            <button type="button"
              onClick={() => setForm({ ...form, return_policy_days: form.return_policy_days && Number(form.return_policy_days) > 0 ? form.return_policy_days : "14" })}
              data-testid="profile-return-mode-window"
              className={`text-sm px-3 py-1.5 rounded-full transition-colors ${Number(form.return_policy_days) > 0 ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground"}`}
            >
              Return window
            </button>
          </div>

          {Number(form.return_policy_days) > 0 && (
            <div className="flex items-center gap-2">
              <Input
                type="number" min="1" max="180" step="1"
                value={form.return_policy_days}
                onChange={e => setForm({ ...form, return_policy_days: e.target.value })}
                placeholder="14"
                data-testid="profile-return-days"
                className="h-10 w-24 mono"
              />
              <span className="text-[13.5px] text-foreground/70">days from delivery to return</span>
            </div>
          )}

          <div className="text-[11.5px] text-foreground/55 inline-flex items-center gap-1.5">
            <Eye className="w-3 h-3"/>
            Buyers will see:{" "}
            <span className="text-foreground/85 font-medium">
              {form.return_policy_days === ""
                ? "Per bilateral agreement"
                : Number(form.return_policy_days) === 0
                  ? "Final sale — no returns accepted"
                  : `${form.return_policy_days}-day return window from delivery`}
            </span>
          </div>
        </div>
      </FieldRow>

      <FieldRow
        label="Offer prebook (deposit + balance flow)"
        hint="Take pre-orders for made-to-order goods. When ON, you can convert any incoming PO into a prebook with a deposit % + worst-case lead time you set per order. Buyer pays the deposit to activate; you're committed to the promised ready date."
      >
        <label className="inline-flex items-center gap-2.5 cursor-pointer select-none" data-testid="profile-offers-prebook-toggle">
          <Switch
            checked={form.offers_prebook}
            onCheckedChange={(v) => setForm({ ...form, offers_prebook: !!v })}
            data-testid="profile-offers-prebook-switch"
          />
          <span className="text-[13.5px] text-foreground/85">
            {form.offers_prebook
              ? "Yes — I accept prebook orders with a deposit"
              : "No — stock-on-hand orders only"}
          </span>
        </label>
      </FieldRow>

      {/* ── Phase C · Social handles ────────────────────────────────── */}
      <SubSectionHeader title="Social handles" desc="Buyers use these as a vibe-check. Paste handles or full URLs — we display them as @handles on your profile."/>

      <FieldRow label="Instagram">
        <Input value={form.social_instagram} onChange={e => setForm({ ...form, social_instagram: e.target.value.slice(0, 80) })}
          placeholder="@glowco" data-testid="profile-social-instagram" className="h-10 max-w-md"/>
      </FieldRow>
      <FieldRow label="TikTok">
        <Input value={form.social_tiktok} onChange={e => setForm({ ...form, social_tiktok: e.target.value.slice(0, 80) })}
          placeholder="@glowco" data-testid="profile-social-tiktok" className="h-10 max-w-md"/>
      </FieldRow>
      <FieldRow label="LinkedIn">
        <Input value={form.social_linkedin} onChange={e => setForm({ ...form, social_linkedin: e.target.value.slice(0, 80) })}
          placeholder="company/glowco" data-testid="profile-social-linkedin" className="h-10 max-w-md"/>
      </FieldRow>
      <FieldRow label="X / Twitter">
        <Input value={form.social_x} onChange={e => setForm({ ...form, social_x: e.target.value.slice(0, 80) })}
          placeholder="@glowco" data-testid="profile-social-x" className="h-10 max-w-md"/>
      </FieldRow>
      <FieldRow label="Facebook">
        <Input value={form.social_facebook} onChange={e => setForm({ ...form, social_facebook: e.target.value.slice(0, 80) })}
          placeholder="glowcobeauty" data-testid="profile-social-facebook" className="h-10 max-w-md"/>
      </FieldRow>
      <FieldRow label="YouTube">
        <Input value={form.social_youtube} onChange={e => setForm({ ...form, social_youtube: e.target.value.slice(0, 80) })}
          placeholder="@glowco" data-testid="profile-social-youtube" className="h-10 max-w-md"/>
      </FieldRow>

      {/* ── Logo + brand colors hand-off ────────────────────────────── */}
      <SubSectionHeader
        title="Logo & brand colors"
        desc="Managed once under Branding — they automatically appear on your public profile, every invoice, and every pack list."
      />
      <div className="rounded-xl border border-border bg-secondary/30 p-4 flex items-center gap-4 flex-wrap" data-testid="profile-branding-card">
        <div className="w-14 h-14 rounded-lg bg-white border border-border grid place-items-center overflow-hidden flex-shrink-0">
          {tenant.branding?.logo_url
            ? <img src={tenant.branding.logo_url} alt="" className="w-full h-full object-contain"/>
            : <span className="text-foreground/35 text-[11px] uppercase tracking-wide">Logo</span>}
        </div>
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center gap-2 text-[12.5px]">
            <span className="w-4 h-4 rounded border border-foreground/15" style={{ background: tenant.branding?.primary_color || "#0a1f1c" }}/>
            <span className="font-mono text-foreground/65">{tenant.branding?.primary_color || "#0a1f1c"}</span>
          </span>
          <span className="inline-flex items-center gap-2 text-[12.5px]">
            <span className="w-4 h-4 rounded border border-foreground/15" style={{ background: tenant.branding?.accent_color || "#e8ff3a" }}/>
            <span className="font-mono text-foreground/65">{tenant.branding?.accent_color || "#e8ff3a"}</span>
          </span>
        </div>
        <a
          href="#branding"
          onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("settings:open-tab", { detail: { id: "branding" } })); }}
          className="ml-auto inline-flex items-center gap-1.5 text-[12.5px] text-foreground/75 hover:text-foreground"
          data-testid="profile-branding-edit-link"
        >
          Edit in Branding <ExternalLink className="w-3 h-3"/>
        </a>
      </div>

      <FieldRow
        label="Custom terms & conditions"
        hint="Stamped on every invoice and pack-list PDF the buyer downloads. Use this for MOQs, MAP enforcement, warranty, force-majeure, and anything else outside the structured policy fields. Plain text — line breaks preserved. Max 8,000 chars."
      >
        <Textarea
          value={form.terms_and_conditions}
          onChange={e => setForm({ ...form, terms_and_conditions: e.target.value.slice(0, 8000) })}
          placeholder={"• Minimum order: $500 wholesale\n• MAP enforced — no resale below MSRP\n• Warranty: 12 months from delivery\n• Force majeure: see MSA section 9.2"}
          data-testid="profile-terms-and-conditions"
          rows={6}
          className="font-mono text-[12.5px]"
        />
        <div className="text-[11px] text-foreground/55 mt-1.5">
          {form.terms_and_conditions.length} / 8000 chars
        </div>
      </FieldRow>

      <div className="mt-6">
        <Button onClick={save} disabled={saving} data-testid="profile-save"
          className="rounded-full bg-foreground text-background hover:bg-foreground/90">
          {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/>Saving…</> : "Save profile"}
        </Button>
      </div>
    </div>
  );
}



// ─────────────────────────────────────────────────────────────────────────────
// CfTxtRecordCard — copy-friendly card for a single Cloudflare-for-SaaS
// validation TXT record. Surfaces both records the customer needs to add
// on their DNS to avoid Error 1014.
//
// We render the value in a soft-wrapping monospace block + give a 1-click
// Copy button per field so the seller can paste straight into their DNS
// provider without manual error.
// ─────────────────────────────────────────────────────────────────────────────
function CfTxtRecordCard({ label, type, name, value, testid }) {
  const copy = (s) => {
    if (!s) return;
    try {
      navigator.clipboard.writeText(s);
      toast.success("Copied");
    } catch { /* no-op */ }
  };
  if (!name || !value) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-white p-3.5" data-testid={testid}>
      <div className="text-[10.5px] mono uppercase tracking-widest text-amber-800/70 mb-2">{label}</div>
      <div className="grid grid-cols-[60px_1fr] gap-x-3 gap-y-2 text-[12px]">
        <div className="text-foreground/55">Type</div>
        <div className="font-mono text-foreground/85">{type || "TXT"}</div>

        <div className="text-foreground/55">Name</div>
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-mono text-foreground/85 break-all min-w-0 flex-1">{name}</span>
          <button type="button" onClick={() => copy(name)} className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] border border-foreground/15 hover:bg-foreground/5">
            <Copy className="w-2.5 h-2.5"/>Copy
          </button>
        </div>

        <div className="text-foreground/55">Value</div>
        <div className="flex items-start gap-2 min-w-0">
          <span className="font-mono text-foreground/85 break-all min-w-0 flex-1">{value}</span>
          <button type="button" onClick={() => copy(value)} className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10.5px] border border-foreground/15 hover:bg-foreground/5">
            <Copy className="w-2.5 h-2.5"/>Copy
          </button>
        </div>
      </div>
    </div>
  );
}

